/**
 * 自定义积木注册
 * @module features/blockly/registerBlocks
 */

import * as Blockly from 'blockly';
import { javascriptGenerator } from 'blockly/javascript';
import { pythonGenerator } from 'blockly/python';
import { YM_XXX_UUID } from '@/features/devices/ym_xxx_uuid';
import { registerScratchExtensionBlocks, registerScratchExtensionGenerators } from '@/features/scratch/scratchExtensionBlocks';

let registered = false;

const getWirelessHandleType = (peripheral: string) => `PeripheralHandle:${peripheral}`;
const getWirelessObjectType = (peripheral: string) => `PeripheralObject:${peripheral}`;
type GeneratorWithScrub = {
  scrub_: (block: Blockly.Block, code: string, thisOnly?: boolean) => string;
};
const JSON_NODE_BLOCK_TYPE = 'JsonNode';
const JSON_ARRAY_VARIABLE_TYPE = 'JsonArray';
const UI_OBJECT_KINDS = ['screen', 'label', 'button', 'input', 'slider', 'progress', 'image', 'checkbox', 'switch', 'arc', 'dropdown', 'list'] as const;
type UiObjectKind = typeof UI_OBJECT_KINDS[number];
const getUiObjectType = (kind: UiObjectKind) => `UIObject:${kind}`;
const getUiObjectCompatibleTypes = () => Array.from(new Set([
  'UIObject',
  ...UI_OBJECT_KINDS.map((kind) => getUiObjectType(kind)),
]));
const getUiParentCompatibleTypes = () => Array.from(new Set([
  getUiObjectType('screen'),
  ...getUiObjectCompatibleTypes(),
]));
const getUiSimpleCompatibleTypes = () => [
  getUiObjectType('screen'),
  getUiObjectType('label'),
];
const UI_SIMPLE_COLOR_PRESETS = [
  ['白色', 'COLOR_WHITE'],
  ['深绿色', 'COLOR_DARK_GREEN'],
  ['深灰色', 'COLOR_DARK_GRAY'],
  ['微信绿', 'COLOR_WECHAT_GREEN'],
  ['中灰色', 'COLOR_MEDIUM_GRAY'],
  ['浅灰色', 'COLOR_LIGHT_GRAY'],
  ['红色', 'COLOR_RED'],
  ['黄色', 'COLOR_YELLOW'],
  ['绿色', 'COLOR_GREEN'],
  ['蓝色', 'COLOR_BLUE'],
  ['黑色', 'COLOR_BLACK'],
  ['自定义', 'CUSTOM'],
] as const;

const UI_SIMPLE_COLOR_VALUES: Record<string, string> = {
  COLOR_WHITE: '0xFFFFFF',
  COLOR_DARK_GREEN: '0x1A6C37',
  COLOR_DARK_GRAY: '0x666666',
  COLOR_WECHAT_GREEN: '0x95EC69',
  COLOR_MEDIUM_GRAY: '0x2A2A2A',
  COLOR_LIGHT_GRAY: '0xE0E0E0',
  COLOR_RED: '0xFF0000',
  COLOR_YELLOW: '0xFFFF00',
  COLOR_GREEN: '0x00FF00',
  COLOR_BLUE: '0x0000FF',
  COLOR_BLACK: '0x000000',
};
const UI_SIMPLE_FONT_OPTIONS = [
  ['小号文字', 'FONT_SMALL'],
  ['中号文字', 'FONT_MEDIUM'],
  ['大号文字', 'FONT_LARGE'],
  ['小号符号', 'FONT_ICON_SMALL'],
  ['中号符号', 'FONT_ICON_MEDIUM'],
  ['大号符号', 'FONT_ICON_LARGE'],
] as const;
const UI_SIMPLE_FONT_VALUES: Record<string, string> = {
  FONT_SMALL: '0',
  FONT_MEDIUM: '1',
  FONT_LARGE: '2',
  FONT_ICON_SMALL: '3',
  FONT_ICON_MEDIUM: '4',
  FONT_ICON_LARGE: '5',
};
const UI_SIMPLE_ALIGN_OPTIONS = [
  ['默认对齐', 'ALIGN_DEFAULT'],
  ['顶部左对齐', 'ALIGN_TOP_LEFT'],
  ['顶部居中', 'ALIGN_TOP_MID'],
  ['顶部右对齐', 'ALIGN_TOP_RIGHT'],
  ['底部左对齐', 'ALIGN_BOTTOM_LEFT'],
  ['底部居中', 'ALIGN_BOTTOM_MID'],
  ['底部右对齐', 'ALIGN_BOTTOM_RIGHT'],
  ['左侧居中', 'ALIGN_LEFT_MID'],
  ['右侧居中', 'ALIGN_RIGHT_MID'],
  ['中心对齐', 'ALIGN_CENTER'],
  ['最大值', 'ALIGN_MAX'],
] as const;
const UI_SIMPLE_ALIGN_VALUES: Record<string, string> = {
  ALIGN_DEFAULT: '0',
  ALIGN_TOP_LEFT: '1',
  ALIGN_TOP_MID: '2',
  ALIGN_TOP_RIGHT: '3',
  ALIGN_BOTTOM_LEFT: '4',
  ALIGN_BOTTOM_MID: '5',
  ALIGN_BOTTOM_RIGHT: '6',
  ALIGN_LEFT_MID: '7',
  ALIGN_RIGHT_MID: '8',
  ALIGN_CENTER: '9',
  ALIGN_MAX: '10',
};
const UI_LVGL_LABEL_ALIGN_OPTIONS = [
  ['顶部左对齐', 'LV_ALIGN_TOP_LEFT'],
  ['顶部居中', 'LV_ALIGN_TOP_MID'],
  ['顶部右对齐', 'LV_ALIGN_TOP_RIGHT'],
  ['左侧居中', 'LV_ALIGN_LEFT_MID'],
  ['中心对齐', 'LV_ALIGN_CENTER'],
  ['右侧居中', 'LV_ALIGN_RIGHT_MID'],
  ['底部左对齐', 'LV_ALIGN_BOTTOM_LEFT'],
  ['底部居中', 'LV_ALIGN_BOTTOM_MID'],
  ['底部右对齐', 'LV_ALIGN_BOTTOM_RIGHT'],
] as const;
const UI_LVGL_LABEL_FONT_OPTIONS = [
  ['14号中文', 'font_puhui_14_1'],
  ['20号中文', 'font_puhui_20_4'],
  ['14号Awsome图标', 'font_awesome_14_1'],
  ['16号Awsome图标', 'font_awesome_16'],
  ['30号Awsome图标', 'font_awesome_30_4'],
] as const;
const UI_LVGL_OBJ_SCROLLBAR_MODE_OPTIONS = [
  ['自动', 'LV_SCROLLBAR_MODE_AUTO'],
  ['常显', 'LV_SCROLLBAR_MODE_ON'],
  ['常隐', 'LV_SCROLLBAR_MODE_OFF'],
  ['滚动时显示', 'LV_SCROLLBAR_MODE_ACTIVE'],
] as const;
const UI_LVGL_OBJ_FLAG_OPTIONS = [
  ['可点击', 'LV_OBJ_FLAG_CLICKABLE'],
  ['可滚动', 'LV_OBJ_FLAG_SCROLLABLE'],
  ['隐藏', 'LV_OBJ_FLAG_HIDDEN'],
  ['可聚焦', 'LV_OBJ_FLAG_CLICK_FOCUSABLE'],
  ['忽略布局', 'LV_OBJ_FLAG_IGNORE_LAYOUT'],
] as const;
const UI_LVGL_LABEL_LONG_MODE_OPTIONS = [
  ['自动换行', 'LV_LABEL_LONG_WRAP'],
  ['省略号', 'LV_LABEL_LONG_DOT'],
  ['滚动', 'LV_LABEL_LONG_SCROLL'],
  ['循环滚动', 'LV_LABEL_LONG_SCROLL_CIRCULAR'],
  ['裁剪', 'LV_LABEL_LONG_CLIP'],
] as const;
const UI_LVGL_SLIDER_MODE_OPTIONS = [
  ['普通', 'LV_SLIDER_MODE_NORMAL'],
  ['对称', 'LV_SLIDER_MODE_SYMMETRICAL'],
  ['范围', 'LV_SLIDER_MODE_RANGE'],
] as const;
const UI_LVGL_BAR_MODE_OPTIONS = [
  ['普通', 'LV_BAR_MODE_NORMAL'],
  ['对称', 'LV_BAR_MODE_SYMMETRICAL'],
  ['范围', 'LV_BAR_MODE_RANGE'],
] as const;
const UI_LVGL_ARC_MODE_OPTIONS = [
  ['普通', 'LV_ARC_MODE_NORMAL'],
  ['对称', 'LV_ARC_MODE_SYMMETRICAL'],
  ['反向', 'LV_ARC_MODE_REVERSE'],
] as const;
const UI_LVGL_LIST_SYMBOL_OPTIONS = [
  ['无图标', 'NULL'],
  ['确认', 'LV_SYMBOL_OK'],
  ['关闭', 'LV_SYMBOL_CLOSE'],
  ['设置', 'LV_SYMBOL_SETTINGS'],
  ['文件', 'LV_SYMBOL_FILE'],
] as const;
const UI_LVGL_BUTTON_CREATE_API_OPTIONS = [
  ['lv_btn_create', 'lv_btn_create'],
  ['lv_button_create', 'lv_button_create'],
] as const;
const UI_LVGL_LABEL_CREATE_TOOLTIP = '创建标签并设置初始文本。';
const UI_LVGL_LABEL_SET_TEXT_TOOLTIP = '设置标签文本。';
const UI_AWSOME_FONT_HINT = '检测到 Awsome 图标字符，请选择 Awsome 图标字体（14/16/30）。';
const UI_AWSOME_TEXT_COLOR = '#FF4D4F';
const UI_AWSOME_ICON_REGEX = /[\uF000-\uF8FF]/u;

const toRgb24Literal = (hexColor: string): string => {
  const normalized = hexColor.trim().replace('#', '');
  const rgb = normalized.length === 3
    ? normalized.split('').map((ch) => `${ch}${ch}`).join('')
    : normalized.padStart(6, '0').slice(-6);
  return `0x${rgb.toUpperCase()}`;
};

const toHexColorFromRgbLiteral = (rgbLiteral: string): string => {
  const normalized = rgbLiteral.trim().toUpperCase().replace(/^0X/, '').padStart(6, '0').slice(-6);
  return `#${normalized}`;
};

const getUiSimpleColorLiteralFromBlock = (
  block: Blockly.Block,
  generator: Blockly.CodeGenerator,
  inputName: string,
  fallback = '0xFFFFFF'
): string => {
  const preset = block.getFieldValue('COLOR_PRESET');
  if (preset === 'CUSTOM') {
    const customColor = block.getFieldValue('CUSTOM_COLOR') || '#FFFFFF';
    return toRgb24Literal(customColor);
  }
  if (preset && UI_SIMPLE_COLOR_VALUES[preset]) {
    return UI_SIMPLE_COLOR_VALUES[preset];
  }
  const fromInput = generator.valueToCode(block, inputName, 0);
  return fromInput || fallback;
};

const createUiSimpleColorPresetField = (block: Blockly.Block) => new Blockly.FieldDropdown(
  UI_SIMPLE_COLOR_PRESETS as unknown as [string, string][],
  (newValue) => {
    const presetColor = UI_SIMPLE_COLOR_VALUES[newValue];
    if (presetColor) {
      setTimeout(() => {
        const customField = block.getField('CUSTOM_COLOR') as unknown as { setValue?: (value: string) => void } | null;
        customField?.setValue?.(toHexColorFromRgbLiteral(presetColor));
      }, 0);
    }
    if (newValue === 'CUSTOM') {
      setTimeout(() => {
        const customField = block.getField('CUSTOM_COLOR') as unknown as { showEditor_?: () => void } | null;
        customField?.showEditor_?.();
      }, 0);
    }
    return newValue;
  }
);

const getUiSimpleFontValueFromBlock = (
  block: Blockly.Block,
  generator: Blockly.CodeGenerator,
  fallback = '1'
): string => {
  const fromField = block.getFieldValue('FONT');
  if (fromField !== null && fromField !== undefined && fromField !== '') {
    return UI_SIMPLE_FONT_VALUES[fromField] || fromField;
  }
  const fromInput = generator.valueToCode(block, 'FONT', 0);
  return fromInput || fallback;
};

const getUiSimpleAlignValueFromBlock = (
  block: Blockly.Block,
  generator: Blockly.CodeGenerator,
  fallback = '0'
): string => {
  const fromField = block.getFieldValue('ALIGN');
  if (fromField !== null && fromField !== undefined && fromField !== '') {
    return UI_SIMPLE_ALIGN_VALUES[fromField] || fromField;
  }
  const fromInput = generator.valueToCode(block, 'ALIGN', 0);
  return fromInput || fallback;
};

const hasAwsomeIconText = (value: string): boolean => UI_AWSOME_ICON_REGEX.test(value);

const setTextBlockFillColor = (textBlock: Blockly.Block, color: string): void => {
  const textField = textBlock.getField('TEXT') as (Blockly.Field & { textElement_?: SVGTextElement | null }) | null;
  const textElement = textField?.textElement_;
  if (textElement) {
    textElement.style.fill = color;
  }
};

const refreshUiAwsomeFontHints = (workspace: Blockly.Workspace | null): void => {
  if (!workspace) return;

  const allBlocks = workspace.getAllBlocks(false);

  allBlocks
    .filter((candidate) => candidate.type === 'text')
    .forEach((candidate) => setTextBlockFillColor(candidate, ''));

  allBlocks
    .filter((candidate) => candidate.type === 'yc_ui_label_create' || candidate.type === 'yc_ui_label_set_text')
    .forEach((candidate) => {
      const baseTooltip = candidate.type === 'yc_ui_label_create'
        ? UI_LVGL_LABEL_CREATE_TOOLTIP
        : UI_LVGL_LABEL_SET_TEXT_TOOLTIP;

      candidate.setWarningText(null);
      candidate.setTooltip(baseTooltip);

      const textBlock = candidate.getInputTargetBlock('TEXT');
      if (!textBlock || textBlock.type !== 'text') {
        return;
      }

      const textValue = String(textBlock.getFieldValue('TEXT') || '');
      if (!hasAwsomeIconText(textValue)) {
        return;
      }

      setTextBlockFillColor(textBlock, UI_AWSOME_TEXT_COLOR);
      candidate.setWarningText(UI_AWSOME_FONT_HINT);
      candidate.setTooltip(`${baseTooltip} ${UI_AWSOME_FONT_HINT}`);
    });
};

const toHexUuid = (value: number) => `0x${value.toString(16).toUpperCase().padStart(4, '0')}`;
const setVariableFieldTypes = (field: Blockly.FieldVariable | null, types: string[], defaultType: string) => {
  if (!field) return;
  const fieldWithSetTypes = field as unknown as {
    setTypes?: (newTypes: string[], defaultTypeName?: string) => void;
  };
  fieldWithSetTypes.setTypes?.(types, defaultType);
};

const createUiParentVariableField = () => new Blockly.FieldVariable(
  'ui_screen_1',
  undefined,
  getUiParentCompatibleTypes(),
  getUiObjectType('screen')
);

const applyWirelessModelTypes = (block: Blockly.Block) => {
  const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
  const handleType = getWirelessHandleType(peripheral);
  const objectType = getWirelessObjectType(peripheral);
  const compatibleHandleTypes = Array.from(new Set([
    handleType,
    'PeripheralHandle',
    getWirelessHandleType('YM-LED'),
    getWirelessHandleType('YM-AU'),
    getWirelessHandleType('YM-PIR'),
    getWirelessHandleType('YM-DLGT'),
    getWirelessHandleType('YM-BEEP'),
    getWirelessHandleType('YM-RYG'),
    getWirelessHandleType('YM-K3'),
    getWirelessHandleType('YM-SWUSB'),
    getWirelessHandleType('YM-TH1'),
    getWirelessHandleType('YM-GNSS'),
    getWirelessHandleType('YM-US'),
    getWirelessHandleType('YM-5G'),
  ]));
  const compatibleObjectTypes = Array.from(new Set([
    objectType,
    'PeripheralObject',
    getWirelessObjectType('YM-LED'),
    getWirelessObjectType('YM-AU'),
    getWirelessObjectType('YM-PIR'),
    getWirelessObjectType('YM-DLGT'),
    getWirelessObjectType('YM-BEEP'),
    getWirelessObjectType('YM-RYG'),
    getWirelessObjectType('YM-K3'),
    getWirelessObjectType('YM-SWUSB'),
    getWirelessObjectType('YM-TH1'),
    getWirelessObjectType('YM-GNSS'),
    getWirelessObjectType('YM-US'),
    getWirelessObjectType('YM-5G'),
  ]));

  if (block.type === 'wireless_peripheral_connect') {
    block.setOutput(true, compatibleHandleTypes);
    return;
  }

  if (block.type === 'wireless_peripheral_disconnect') {
    const objVarField = block.getField('OBJ_VAR') as Blockly.FieldVariable | null;
    setVariableFieldTypes(objVarField, [...compatibleObjectTypes, ...compatibleHandleTypes], objectType);
    return;
  }

  if (block.type === 'wireless_peripheral_set_object') {
    const handleInput = block.getInput('HANDLE');
    handleInput?.setCheck(compatibleHandleTypes);
    const objVarField = block.getField('OBJ_VAR') as Blockly.FieldVariable | null;
    setVariableFieldTypes(objVarField, compatibleObjectTypes, objectType);
  }

  if (block.type === 'wireless_peripheral_create_object') {
    const objVarField = block.getField('OBJ_VAR') as Blockly.FieldVariable | null;
    setVariableFieldTypes(objVarField, compatibleObjectTypes, objectType);
  }
};

const ensureRegistered = () => {
  if (registered) return;
  registered = true;

  const iotJsonBlockColour = 225;
  const jsonBooleanChecks = ['Boolean', 'Number'];
  const jsonAnyOutputChecks = [JSON_NODE_BLOCK_TYPE, JSON_ARRAY_VARIABLE_TYPE];
  const jsonObjectVariableTypes = [JSON_NODE_BLOCK_TYPE, ''];
  const jsonArrayVariableTypes = [JSON_ARRAY_VARIABLE_TYPE, ''];
  const jsonAnyVariableTypes = [JSON_NODE_BLOCK_TYPE, JSON_ARRAY_VARIABLE_TYPE, ''];
  const appendJsonBlockInput = (
    block: Blockly.Block,
    inputName: string,
    label: string,
    check: string | string[] | null = null
  ) => {
    const input = block.appendValueInput(inputName);
    if (check !== null) {
      input.setCheck(check);
    }
    input.appendField(label);
  };

  const createJsonVariableField = (
    defaultName: string,
    variableType: string,
    compatibleTypes: string[] = [variableType, '']
  ) => (
    new Blockly.FieldVariable(defaultName, undefined, compatibleTypes, variableType)
  );

  const appendJsonVariableField = (
    block: Blockly.Block,
    fieldName: string,
    label: string,
    defaultName: string,
    variableType: string,
    compatibleTypes: string[] = [variableType, '']
  ) => {
    block.appendDummyInput()
      .appendField(label)
      .appendField(createJsonVariableField(defaultName, variableType, compatibleTypes), fieldName);
  };

  // ============ IoT C API Blocks ============
  Blockly.Blocks['iot_wifi_enable'] = {
    init() {
      this.appendDummyInput().appendField('启用 WiFi');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(195);
      this.setTooltip('生成 wifi_enable()');
    },
  };

  Blockly.Blocks['iot_wifi_set_ssid_password'] = {
    init() {
      this.appendDummyInput().appendField('设置 WiFi 账号');
      this.appendValueInput('SSID')
        .setCheck('String')
        .appendField('SSID');
      this.appendValueInput('PASSWORD')
        .setCheck('String')
        .appendField('密码');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(195);
      this.setTooltip('生成 wifi_set_ssid_password(ssid, password)');
    },
  };

  Blockly.Blocks['iot_wifi_connect'] = {
    init() {
      this.appendDummyInput().appendField('连接 WiFi');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(195);
      this.setTooltip('生成 wifi_connect()');
    },
  };

  Blockly.Blocks['iot_wifi_is_connected'] = {
    init() {
      this.appendDummyInput().appendField('WiFi 已连接');
      this.setOutput(true, 'Boolean');
      this.setColour(195);
      this.setTooltip('生成 wifi_is_connected()');
    },
  };

  Blockly.Blocks['iot_mqtt_init'] = {
    init() {
      this.appendDummyInput().appendField('初始化 MQTT');
      this.appendValueInput('HOST')
        .setCheck('String')
        .appendField('主机');
      this.appendValueInput('PORT')
        .setCheck('Number')
        .appendField('端口');
      this.appendValueInput('CLIENT_ID')
        .setCheck('String')
        .appendField('客户端ID');
      this.appendValueInput('USERNAME')
        .setCheck('String')
        .appendField('用户名');
      this.appendValueInput('PASSWORD')
        .setCheck('String')
        .appendField('密码');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('生成 mqtt_init(host, port, client_id, username, password)');
    },
  };

  Blockly.Blocks['iot_mqtt_connect'] = {
    init() {
      this.appendDummyInput().appendField('连接 MQTT');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('生成 mqtt_connect()');
    },
  };

  Blockly.Blocks['iot_mqtt_is_connected'] = {
    init() {
      this.appendDummyInput().appendField('MQTT 已连接');
      this.setOutput(true, 'Boolean');
      this.setColour(210);
      this.setTooltip('生成 mqtt_is_connected()');
    },
  };

  Blockly.Blocks['iot_mqtt_subscribe'] = {
    init() {
      this.appendDummyInput().appendField('订阅 MQTT');
      this.appendValueInput('TOPIC')
        .setCheck('String')
        .appendField('主题');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('生成 mqtt_subscribe(topic)');
    },
  };

  Blockly.Blocks['iot_mqtt_publish'] = {
    init() {
      this.appendDummyInput().appendField('发布 MQTT');
      this.appendValueInput('TOPIC')
        .setCheck('String')
        .appendField('主题');
      this.appendValueInput('PAYLOAD')
        .setCheck('String')
        .appendField('内容');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('生成 mqtt_publish(topic, payload)');
    },
  };

  Blockly.Blocks['iot_mqtt_disconnect'] = {
    init() {
      this.appendDummyInput().appendField('断开 MQTT');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('生成 mqtt_disconnect()');
    },
  };

  Blockly.Blocks['json_parse'] = {
    init() {
      this.appendValueInput('TEXT')
        .setCheck('String')
        .appendField('解析文本');
      this.setInputsInline(true);
      this.setOutput(true, jsonAnyOutputChecks);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把文本解析为 JSON 节点。C API: json_parse(text)');
    },
  };
  Blockly.Blocks['json_create_object'] = {
    init() {
      this.appendDummyInput().appendField('JSON 对象值');
      this.setInputsInline(true);
      this.setOutput(true, JSON_NODE_BLOCK_TYPE);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('创建一个可直接作为值使用的 JSON 对象。C API: json_create_object()');
    },
  };
  Blockly.Blocks['json_create_array'] = {
    init() {
      this.appendDummyInput().appendField('JSON 数组值');
      this.setInputsInline(true);
      this.setOutput(true, JSON_ARRAY_VARIABLE_TYPE);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('创建一个可直接作为值使用的 JSON 数组。C API: json_create_array()');
    },
  };
  Blockly.Blocks['json_create_object_var'] = {
    init() {
      this.appendDummyInput()
        .appendField('创建 JSON 对象到')
        .appendField(createJsonVariableField('json_object_1', JSON_NODE_BLOCK_TYPE, jsonObjectVariableTypes), 'OBJ_VAR');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('创建 JSON 对象变量。C API: obj = json_create_object()');
    },
  };
  Blockly.Blocks['json_create_array_var'] = {
    init() {
      this.appendDummyInput()
        .appendField('创建 JSON 数组对象到')
        .appendField(createJsonVariableField('json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes), 'ARR_VAR');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('创建 JSON 数组变量。C API: arr = json_create_array()');
    },
  };
  Blockly.Blocks['json_delete'] = {
    init() {
      appendJsonVariableField(this, 'JSON', '删除 JSON 节点', 'json_node_1', JSON_NODE_BLOCK_TYPE, jsonAnyVariableTypes);
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('删除选中的 JSON 节点或数组。C API: json_delete(json)');
    },
  };
  Blockly.Blocks['json_object_get'] = {
    init() {
      appendJsonVariableField(this, 'OBJ', '对象', 'json_object_1', JSON_NODE_BLOCK_TYPE, jsonObjectVariableTypes);
      appendJsonBlockInput(this, 'KEY', '键', 'String');
      this.setInputsInline(true);
      this.setOutput(true, jsonAnyOutputChecks);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('读取对象指定键对应的 JSON 节点。C API: json_object_get(obj, key)');
    },
  };
  Blockly.Blocks['json_object_set_string'] = {
    init() {
      appendJsonVariableField(this, 'OBJ', '对象', 'json_object_1', JSON_NODE_BLOCK_TYPE, jsonObjectVariableTypes);
      appendJsonBlockInput(this, 'KEY', '键', 'String');
      appendJsonBlockInput(this, 'VALUE', '设置为字符串', 'String');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把对象的键设置为字符串。C API: json_object_set_string(obj, key, value)');
    },
  };
  Blockly.Blocks['json_object_set_number'] = {
    init() {
      appendJsonVariableField(this, 'OBJ', '对象', 'json_object_1', JSON_NODE_BLOCK_TYPE, jsonObjectVariableTypes);
      appendJsonBlockInput(this, 'KEY', '键', 'String');
      appendJsonBlockInput(this, 'VALUE', '设置为数值', 'Number');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把对象的键设置为数值。C API: json_object_set_number(obj, key, value)');
    },
  };
  Blockly.Blocks['json_object_set_bool'] = {
    init() {
      appendJsonVariableField(this, 'OBJ', '对象', 'json_object_1', JSON_NODE_BLOCK_TYPE, jsonObjectVariableTypes);
      appendJsonBlockInput(this, 'KEY', '键', 'String');
      appendJsonBlockInput(this, 'VALUE', '设置为布尔值', jsonBooleanChecks);
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把对象的键设置为布尔值。C API: json_object_set_bool(obj, key, value)');
    },
  };
  Blockly.Blocks['json_object_set_null'] = {
    init() {
      appendJsonVariableField(this, 'OBJ', '对象', 'json_object_1', JSON_NODE_BLOCK_TYPE, jsonObjectVariableTypes);
      appendJsonBlockInput(this, 'KEY', '键', 'String');
      this.appendDummyInput().appendField('设置为空值');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把对象的键设置为空值。C API: json_object_set_null(obj, key)');
    },
  };
  Blockly.Blocks['json_object_set_json'] = {
    init() {
      appendJsonVariableField(this, 'OBJ', '对象', 'json_object_1', JSON_NODE_BLOCK_TYPE, jsonObjectVariableTypes);
      appendJsonBlockInput(this, 'KEY', '键', 'String');
      appendJsonVariableField(this, 'CHILD', '设置为 JSON 节点', 'json_node_1', JSON_NODE_BLOCK_TYPE, jsonAnyVariableTypes);
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把对象的键设置为另一个 JSON 节点或数组。C API: json_object_set_json(obj, key, child)');
    },
  };
  Blockly.Blocks['json_array_size'] = {
    init() {
      appendJsonVariableField(this, 'ARR', '数组', 'json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes);
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(iotJsonBlockColour);
      this.setTooltip('获取 JSON 数组长度。C API: json_array_size(arr)');
    },
  };
  Blockly.Blocks['json_array_get'] = {
    init() {
      appendJsonVariableField(this, 'ARR', '数组', 'json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes);
      appendJsonBlockInput(this, 'INDEX', '索引', 'Number');
      this.setInputsInline(true);
      this.setOutput(true, jsonAnyOutputChecks);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('读取数组指定索引的 JSON 节点。C API: json_array_get(arr, index)');
    },
  };
  Blockly.Blocks['json_array_add_string'] = {
    init() {
      appendJsonVariableField(this, 'ARR', '数组', 'json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes);
      appendJsonBlockInput(this, 'VALUE', '追加字符串', 'String');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('向数组追加字符串。C API: json_array_add_string(arr, value)');
    },
  };
  Blockly.Blocks['json_array_add_number'] = {
    init() {
      appendJsonVariableField(this, 'ARR', '数组', 'json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes);
      appendJsonBlockInput(this, 'VALUE', '追加数值', 'Number');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('向数组追加数值。C API: json_array_add_number(arr, value)');
    },
  };
  Blockly.Blocks['json_array_add_bool'] = {
    init() {
      appendJsonVariableField(this, 'ARR', '数组', 'json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes);
      appendJsonBlockInput(this, 'VALUE', '追加布尔值', jsonBooleanChecks);
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('向数组追加布尔值。C API: json_array_add_bool(arr, value)');
    },
  };
  Blockly.Blocks['json_array_add_null'] = {
    init() {
      appendJsonVariableField(this, 'ARR', '数组', 'json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes);
      this.appendDummyInput().appendField('追加空值');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('向数组追加空值。C API: json_array_add_null(arr)');
    },
  };
  Blockly.Blocks['json_array_add_json'] = {
    init() {
      appendJsonVariableField(this, 'ARR', '数组', 'json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes);
      appendJsonVariableField(this, 'CHILD', '追加 JSON 节点', 'json_node_1', JSON_NODE_BLOCK_TYPE, jsonAnyVariableTypes);
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('向数组追加 JSON 节点或数组。C API: json_array_add_json(arr, child)');
    },
  };
  Blockly.Blocks['json_array_remove'] = {
    init() {
      appendJsonVariableField(this, 'ARR', '数组', 'json_array_1', JSON_ARRAY_VARIABLE_TYPE, jsonArrayVariableTypes);
      appendJsonBlockInput(this, 'INDEX', '删除索引', 'Number');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(iotJsonBlockColour);
      this.setTooltip('删除数组指定索引的元素。C API: json_array_remove(arr, index)');
    },
  };
  Blockly.Blocks['json_as_string'] = {
    init() {
      appendJsonVariableField(this, 'JSON', 'JSON 节点', 'json_node_1', JSON_NODE_BLOCK_TYPE, jsonAnyVariableTypes);
      this.appendDummyInput().appendField('转为字符串');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把 JSON 节点转换为字符串。C API: json_as_string(json)');
    },
  };
  Blockly.Blocks['json_as_number'] = {
    init() {
      appendJsonVariableField(this, 'JSON', 'JSON 节点', 'json_node_1', JSON_NODE_BLOCK_TYPE, jsonAnyVariableTypes);
      this.appendDummyInput().appendField('转为数值');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把 JSON 节点转换为数值。C API: json_as_number(json)');
    },
  };
  Blockly.Blocks['json_as_bool'] = {
    init() {
      appendJsonVariableField(this, 'JSON', 'JSON 节点', 'json_node_1', JSON_NODE_BLOCK_TYPE, jsonAnyVariableTypes);
      this.appendDummyInput().appendField('转为布尔值');
      this.setInputsInline(true);
      this.setOutput(true, 'Boolean');
      this.setColour(iotJsonBlockColour);
      this.setTooltip('把 JSON 节点转换为布尔值。C API: json_as_bool(json)');
    },
  };

  Blockly.Blocks['text_number_to_string_custom'] = {
    init() {
      this.appendValueInput('VALUE')
        .setCheck('Number')
        .appendField('数字转文本');
      this.appendDummyInput()
        .appendField('保留小数位')
        .appendField(new Blockly.FieldNumber(6, 0, 20, 1), 'DIGITS');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(160);
      this.setTooltip('将数字转换为文本，并按指定小数位数格式化。');
    },
  };

  Blockly.Blocks['text_concat_two_custom'] = {
    init() {
      this.appendValueInput('LEFT')
        .setCheck('String')
        .appendField('拼接文本');
      this.appendValueInput('RIGHT')
        .setCheck('String')
        .appendField('和');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(160);
      this.setTooltip('将两个文本拼接成一个文本。');
    },
  };

  Blockly.Blocks['json_parse_text'] = {
    init() {
      this.appendValueInput('TEXT')
        .setCheck('String')
        .appendField('解析 JSON 文本');
      this.setInputsInline(true);
      this.setOutput(true, null);
      this.setColour(150);
      this.setTooltip('将 JSON 文本解析为对象。');
    },
  };

  Blockly.Blocks['json_stringify_object'] = {
    init() {
      this.appendValueInput('VALUE')
        .appendField('JSON 节点为字符串');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(150);
      this.setTooltip('将 JSON 节点转换为字符串。');
    },
  };

  Blockly.Blocks['yios_int2str'] = {
    init() {
      this.appendValueInput('VALUE')
        .setCheck('Number')
        .appendField('yiOS 整数转文本');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(175);
      this.setTooltip('调用 yiOS 的 int2str 函数把整数转换为文本。');
    },
  };

  Blockly.Blocks['yios_str2int'] = {
    init() {
      this.appendValueInput('VALUE')
        .setCheck('String')
        .appendField('yiOS 文本转整数');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(175);
      this.setTooltip('调用 yiOS 的 str2int 函数把文本转换为整数。');
    },
  };

  Blockly.Blocks['yios_str2float'] = {
    init() {
      this.appendValueInput('VALUE')
        .setCheck('String')
        .appendField('yiOS 文本转浮点数');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(175);
      this.setTooltip('调用 yiOS 的 str2float 函数把文本转换为浮点数。');
    },
  };

  Blockly.Blocks['yios_float2str'] = {
    init() {
      this.appendValueInput('VALUE')
        .setCheck('Number')
        .appendField('yiOS 浮点数转文本');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(175);
      this.setTooltip('调用 yiOS 的 float2str 函数把浮点数转换为文本。');
    },
  };

  Blockly.Blocks['wireless_peripheral_connect'] = {
    init() {
      this.appendDummyInput()
        .appendField('连接')
        .appendField(new Blockly.FieldLabelSerializable('YM-LED'), 'PERIPHERAL')
        .appendField('-')
        .appendField(
          new Blockly.FieldTextInput('', (value) => value.replace(/[^0-9a-zA-Z]/g, '').slice(0, 6)),
          'DEVICE_ID'
        );
      this.setOutput(true, 'PeripheralHandle:YM-LED');
      this.setColour(200);
      this.setTooltip('连接无线外设并返回连接句柄（可赋值给变量）。输入6位ID时按完整设备名连接，留空时按该型号前缀扫描。');
      applyWirelessModelTypes(this);
    },
    onchange() {
      applyWirelessModelTypes(this);
    },
  };

  Blockly.Blocks['wireless_peripheral_create_object'] = {
    init() {
      this.appendDummyInput()
        .appendField('创建')
        .appendField(new Blockly.FieldLabelSerializable('YM-LED'), 'PERIPHERAL')
        .appendField('变量')
        .appendField(
          new Blockly.FieldVariable('obj', undefined, ['PeripheralObject:YM-LED'], 'PeripheralObject:YM-LED'),
          'OBJ_VAR'
        );
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip('创建无线外设变量（本质是句柄变量）。');
      applyWirelessModelTypes(this);
    },
    onchange() {
      applyWirelessModelTypes(this);
    },
  };

  Blockly.Blocks['wireless_peripheral_set_object'] = {
    init() {
      this.appendDummyInput()
        .appendField('将')
        .appendField(new Blockly.FieldLabelSerializable('YM-LED'), 'PERIPHERAL')
        .appendField('对象')
        .appendField(
          new Blockly.FieldVariable('obj', undefined, ['PeripheralObject:YM-LED'], 'PeripheralObject:YM-LED'),
          'OBJ_VAR'
        )
        .appendField('设为');
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-LED');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip('像标准变量一样，将同型号连接句柄赋值给变量。');
      applyWirelessModelTypes(this);
    },
    onchange() {
      applyWirelessModelTypes(this);
    },
  };

  Blockly.Blocks['wireless_peripheral_disconnect'] = {
    init() {
      this.appendDummyInput()
        .appendField('断开')
        .appendField(new Blockly.FieldLabelSerializable('YM-LED'), 'PERIPHERAL')
        .appendField('对象', 'OBJECT_LABEL')
        .appendField(
          new Blockly.FieldVariable('obj', undefined, ['PeripheralObject:YM-LED'], 'PeripheralObject:YM-LED'),
          'OBJ_VAR'
        );
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip('按对象变量断开对应无线外设连接。');
      applyWirelessModelTypes(this);
    },
    onchange() {
      applyWirelessModelTypes(this);
    },
  };

  Blockly.Blocks['wired_peripheral_action'] = {
    init() {
      this.appendDummyInput()
        .appendField('有线外设')
        .appendField(new Blockly.FieldLabelSerializable('YN-LED'), 'PERIPHERAL')
        .appendField('执行动作');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(120);
      this.setTooltip('有线外设动作占位积木，可用于后续扩展具体能力。');
    },
  };

  Blockly.Blocks['yc_ui_screen_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('创建屏幕对象到')
        .appendField(new Blockly.FieldVariable('ui_screen_1', undefined, [getUiObjectType('screen')], getUiObjectType('screen')), 'OBJ_VAR');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建 LVGL 屏幕对象变量。');
    },
  };

  Blockly.Blocks['yc_ui_set_active_screen'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck([getUiObjectType('screen')])
        .appendField('加载屏幕');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('将指定屏幕设为当前显示屏幕。');
    },
  };

  Blockly.Blocks['yc_ui_label_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建标签对象到')
        .appendField(new Blockly.FieldVariable('ui_label_1', undefined, [getUiObjectType('label')], getUiObjectType('label')), 'OBJ_VAR');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip(UI_LVGL_LABEL_CREATE_TOOLTIP);
    },
    onchange() {
      refreshUiAwsomeFontHints(this.workspace);
    },
  };

  Blockly.Blocks['yc_ui_label_set_text'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置标签')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, [getUiObjectType('label')], getUiObjectType('label')),
          'OBJ_VAR'
        );
      this.appendValueInput('TEXT').setCheck('String').appendField('文本为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip(UI_LVGL_LABEL_SET_TEXT_TOOLTIP);
    },
    onchange() {
      refreshUiAwsomeFontHints(this.workspace);
    },
  };

  Blockly.Blocks['yc_ui_label_set_align'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        );
      this.appendDummyInput()
        .appendField('对齐为')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_LABEL_ALIGN_OPTIONS as unknown as [string, string][]), 'ALIGN');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置对象在父对象内的对齐方式。');
      this.setFieldValue('LV_ALIGN_CENTER', 'ALIGN');
    },
  };

  Blockly.Blocks['yc_ui_label_set_font'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        );
      this.appendDummyInput()
        .appendField('字体')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_LABEL_FONT_OPTIONS as unknown as [string, string][]), 'FONT');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置对象字体。');
      this.setFieldValue('font_puhui_14_1', 'FONT');
    },
  };

  Blockly.Blocks['yc_ui_label_set_text_color'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        );
      this.appendDummyInput()
        .appendField('文字颜色')
        .appendField(new Blockly.FieldColour('#000000'), 'COLOR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置对象文字颜色。');
    },
  };

  Blockly.Blocks['yc_ui_label_set_bg_color'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        );
      this.appendDummyInput()
        .appendField('背景色')
        .appendField(new Blockly.FieldColour('#FFFFFF'), 'COLOR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置对象背景色。');
    },
  };

  Blockly.Blocks['yc_ui_label_set_pos'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        )
        .appendField('位置');
      this.appendValueInput('X').setCheck('Number').appendField('X');
      this.appendValueInput('Y').setCheck('Number').appendField('Y');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置对象位置。');
    },
  };

  Blockly.Blocks['yc_ui_label_set_size'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        )
        .appendField('大小');
      this.appendValueInput('WIDTH').setCheck('Number').appendField('宽');
      this.appendValueInput('HEIGHT').setCheck('Number').appendField('高');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置对象大小（宽度和高度）。');
    },
  };

  Blockly.Blocks['yc_ui_label_set_width'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        );
      this.appendValueInput('WIDTH').setCheck('Number').appendField('宽度');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置对象宽度。');
    },
  };

  Blockly.Blocks['yc_ui_label_set_height'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        );
      this.appendValueInput('HEIGHT').setCheck('Number').appendField('高度');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置对象高度。');
    },
  };

  Blockly.Blocks['yc_ui_obj_center'] = {
    init() {
      this.appendDummyInput()
        .appendField('居中对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        );
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_obj_center，使对象在父对象内居中。');
    },
  };

  Blockly.Blocks['yc_ui_obj_set_scrollbar_mode'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        )
        .appendField('滚动条模式')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_OBJ_SCROLLBAR_MODE_OPTIONS as unknown as [string, string][]), 'MODE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_obj_set_scrollbar_mode。');
      this.setFieldValue('LV_SCROLLBAR_MODE_AUTO', 'MODE');
    },
  };

  Blockly.Blocks['yc_ui_obj_add_flag'] = {
    init() {
      this.appendDummyInput()
        .appendField('给对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        )
        .appendField('添加标志')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_OBJ_FLAG_OPTIONS as unknown as [string, string][]), 'FLAG');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_obj_add_flag。');
      this.setFieldValue('LV_OBJ_FLAG_CLICKABLE', 'FLAG');
    },
  };

  Blockly.Blocks['yc_ui_obj_clear_flag'] = {
    init() {
      this.appendDummyInput()
        .appendField('给对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        )
        .appendField('清除标志')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_OBJ_FLAG_OPTIONS as unknown as [string, string][]), 'FLAG');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_obj_clear_flag。');
      this.setFieldValue('LV_OBJ_FLAG_CLICKABLE', 'FLAG');
    },
  };

  Blockly.Blocks['yc_ui_obj_has_flag'] = {
    init() {
      this.appendDummyInput()
        .appendField('对象')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, getUiObjectCompatibleTypes(), getUiObjectType('label')),
          'OBJ_VAR'
        )
        .appendField('是否有标志')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_OBJ_FLAG_OPTIONS as unknown as [string, string][]), 'FLAG');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(285);
      this.setTooltip('调用 lv_obj_has_flag，返回 1 或 0。');
      this.setFieldValue('LV_OBJ_FLAG_CLICKABLE', 'FLAG');
    },
  };

  Blockly.Blocks['yc_ui_label_set_long_mode'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置标签')
        .appendField(
          new Blockly.FieldVariable('ui_label_1', undefined, [getUiObjectType('label')], getUiObjectType('label')),
          'OBJ_VAR'
        )
        .appendField('长文本模式')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_LABEL_LONG_MODE_OPTIONS as unknown as [string, string][]), 'MODE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_label_set_long_mode。');
      this.setFieldValue('LV_LABEL_LONG_WRAP', 'MODE');
    },
  };

  Blockly.Blocks['yc_ui_button_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建按钮对象')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_BUTTON_CREATE_API_OPTIONS as unknown as [string, string][]), 'API')
        .appendField('到')
        .appendField(new Blockly.FieldVariable('ui_button_1', undefined, [getUiObjectType('button')], getUiObjectType('button')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建按钮对象。');
      this.setFieldValue('lv_btn_create', 'API');
    },
  };

  Blockly.Blocks['yc_ui_button_set_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('button')]).appendField('设置按钮');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置按钮文本。');
    },
  };

  Blockly.Blocks['yc_ui_checkbox_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建复选框对象到')
        .appendField(new Blockly.FieldVariable('ui_checkbox_1', undefined, [getUiObjectType('checkbox')], getUiObjectType('checkbox')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建复选框对象。');
    },
  };

  Blockly.Blocks['yc_ui_checkbox_set_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('checkbox')]).appendField('设置复选框');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_checkbox_set_text。');
    },
  };

  Blockly.Blocks['yc_ui_checkbox_get_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('checkbox')]).appendField('获取复选框文本');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(285);
      this.setTooltip('调用 lv_checkbox_get_text。');
    },
  };

  Blockly.Blocks['yc_ui_switch_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建开关对象到')
        .appendField(new Blockly.FieldVariable('ui_switch_1', undefined, [getUiObjectType('switch')], getUiObjectType('switch')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建开关对象。');
    },
  };

  Blockly.Blocks['yc_ui_input_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建输入框对象到')
        .appendField(new Blockly.FieldVariable('ui_input_1', undefined, [getUiObjectType('input')], getUiObjectType('input')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建输入框对象。');
    },
  };

  Blockly.Blocks['yc_ui_input_set_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('input')]).appendField('设置输入框');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置输入框文本。');
    },
  };

  Blockly.Blocks['yc_ui_input_add_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('input')]).appendField('给输入框追加文本');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_textarea_add_text。');
    },
  };

  Blockly.Blocks['yc_ui_input_get_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('input')]).appendField('获取输入框文本');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(285);
      this.setTooltip('调用 lv_textarea_get_text。');
    },
  };

  Blockly.Blocks['yc_ui_input_set_placeholder'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('input')]).appendField('设置输入框占位文本');
      this.appendValueInput('TEXT').setCheck('String').appendField('为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_textarea_set_placeholder_text。');
    },
  };

  Blockly.Blocks['yc_ui_slider_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建滑块对象到')
        .appendField(new Blockly.FieldVariable('ui_slider_1', undefined, [getUiObjectType('slider')], getUiObjectType('slider')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建滑块对象。');
    },
  };

  Blockly.Blocks['yc_ui_slider_set_value'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('slider')]).appendField('设置滑块');
      this.appendValueInput('VALUE').setCheck('Number').appendField('值为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置滑块当前值。');
    },
  };

  Blockly.Blocks['yc_ui_slider_set_mode'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('slider')]).appendField('设置滑块模式');
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown(UI_LVGL_SLIDER_MODE_OPTIONS as unknown as [string, string][]), 'MODE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_slider_set_mode。');
      this.setFieldValue('LV_SLIDER_MODE_NORMAL', 'MODE');
    },
  };

  Blockly.Blocks['yc_ui_slider_get_value'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('slider')]).appendField('获取滑块值');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(285);
      this.setTooltip('调用 lv_slider_get_value。');
    },
  };

  Blockly.Blocks['yc_ui_progress_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建进度条对象到')
        .appendField(new Blockly.FieldVariable('ui_progress_1', undefined, [getUiObjectType('progress')], getUiObjectType('progress')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建进度条对象。');
    },
  };

  Blockly.Blocks['yc_ui_progress_set_value'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('progress')]).appendField('设置进度条');
      this.appendValueInput('VALUE').setCheck('Number').appendField('值为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置进度条值。');
    },
  };

  Blockly.Blocks['yc_ui_progress_set_range'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('progress')]).appendField('设置进度条范围');
      this.appendValueInput('MIN').setCheck('Number').appendField('最小');
      this.appendValueInput('MAX').setCheck('Number').appendField('最大');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_bar_set_range。');
    },
  };

  Blockly.Blocks['yc_ui_progress_set_mode'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('progress')]).appendField('设置进度条模式');
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown(UI_LVGL_BAR_MODE_OPTIONS as unknown as [string, string][]), 'MODE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_bar_set_mode。');
      this.setFieldValue('LV_BAR_MODE_NORMAL', 'MODE');
    },
  };

  Blockly.Blocks['yc_ui_progress_get_value'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('progress')]).appendField('获取进度条值');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(285);
      this.setTooltip('调用 lv_bar_get_value。');
    },
  };

  Blockly.Blocks['yc_ui_image_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建图片对象到')
        .appendField(new Blockly.FieldVariable('ui_image_1', undefined, [getUiObjectType('image')], getUiObjectType('image')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建图片对象。');
    },
  };

  Blockly.Blocks['yc_ui_image_set_src'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('image')]).appendField('设置图片');
      this.appendValueInput('SRC').setCheck('String').appendField('源为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('设置图片资源路径。');
    },
  };

  Blockly.Blocks['yc_ui_arc_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建圆弧对象到')
        .appendField(new Blockly.FieldVariable('ui_arc_1', undefined, [getUiObjectType('arc')], getUiObjectType('arc')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建圆弧对象。');
    },
  };

  Blockly.Blocks['yc_ui_arc_set_value'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('arc')]).appendField('设置圆弧值');
      this.appendValueInput('VALUE').setCheck('Number').appendField('为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_arc_set_value。');
    },
  };

  Blockly.Blocks['yc_ui_arc_set_range'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('arc')]).appendField('设置圆弧范围');
      this.appendValueInput('MIN').setCheck('Number').appendField('最小');
      this.appendValueInput('MAX').setCheck('Number').appendField('最大');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_arc_set_range。');
    },
  };

  Blockly.Blocks['yc_ui_arc_set_angles'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('arc')]).appendField('设置圆弧角度');
      this.appendValueInput('START').setCheck('Number').appendField('开始');
      this.appendValueInput('END').setCheck('Number').appendField('结束');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_arc_set_angles。');
    },
  };

  Blockly.Blocks['yc_ui_arc_set_bg_angles'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('arc')]).appendField('设置圆弧背景角度');
      this.appendValueInput('START').setCheck('Number').appendField('开始');
      this.appendValueInput('END').setCheck('Number').appendField('结束');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_arc_set_bg_angles。');
    },
  };

  Blockly.Blocks['yc_ui_arc_set_rotation'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('arc')]).appendField('设置圆弧旋转角');
      this.appendValueInput('ROTATION').setCheck('Number').appendField('为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_arc_set_rotation。');
    },
  };

  Blockly.Blocks['yc_ui_arc_set_mode'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('arc')]).appendField('设置圆弧模式');
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown(UI_LVGL_ARC_MODE_OPTIONS as unknown as [string, string][]), 'MODE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_arc_set_mode。');
      this.setFieldValue('LV_ARC_MODE_NORMAL', 'MODE');
    },
  };

  Blockly.Blocks['yc_ui_arc_get_value'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('arc')]).appendField('获取圆弧值');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(285);
      this.setTooltip('调用 lv_arc_get_value。');
    },
  };

  Blockly.Blocks['yc_ui_dropdown_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建下拉框对象到')
        .appendField(new Blockly.FieldVariable('ui_dropdown_1', undefined, [getUiObjectType('dropdown')], getUiObjectType('dropdown')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建下拉框对象。');
    },
  };

  Blockly.Blocks['yc_ui_dropdown_set_options'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('dropdown')]).appendField('设置下拉框选项');
      this.appendValueInput('OPTIONS').setCheck('String').appendField('为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_dropdown_set_options（使用换行分隔选项）。');
    },
  };

  Blockly.Blocks['yc_ui_dropdown_add_option'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('dropdown')]).appendField('给下拉框添加选项');
      this.appendValueInput('OPTION').setCheck('String').appendField('文本');
      this.appendValueInput('POSITION').setCheck('Number').appendField('位置');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_dropdown_add_option。');
    },
  };

  Blockly.Blocks['yc_ui_dropdown_clear_options'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('dropdown')]).appendField('清空下拉框选项');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_dropdown_clear_options。');
    },
  };

  Blockly.Blocks['yc_ui_dropdown_set_selected'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('dropdown')]).appendField('设置下拉框选中项');
      this.appendValueInput('SELECTED').setCheck('Number').appendField('索引');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('调用 lv_dropdown_set_selected。');
    },
  };

  Blockly.Blocks['yc_ui_dropdown_get_selected'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('dropdown')]).appendField('获取下拉框选中索引');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(285);
      this.setTooltip('调用 lv_dropdown_get_selected。');
    },
  };

  Blockly.Blocks['yc_ui_dropdown_get_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('dropdown')]).appendField('获取下拉框文本');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(285);
      this.setTooltip('调用 lv_dropdown_get_text。');
    },
  };

  Blockly.Blocks['yc_ui_list_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('在父对象')
        .appendField(createUiParentVariableField(), 'PARENT_VAR');
      this.appendDummyInput()
        .appendField('创建列表对象到')
        .appendField(new Blockly.FieldVariable('ui_list_1', undefined, [getUiObjectType('list')], getUiObjectType('list')), 'OBJ_VAR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('创建列表对象。');
    },
  };

  Blockly.Blocks['yc_ui_list_add_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('list')]).appendField('列表添加文本项');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本');
      this.setInputsInline(true);
      this.setOutput(true, getUiObjectCompatibleTypes());
      this.setColour(285);
      this.setTooltip('调用 lv_list_add_text，返回创建的项对象。');
    },
  };

  Blockly.Blocks['yc_ui_list_add_btn'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('list')]).appendField('列表添加按钮项');
      this.appendDummyInput()
        .appendField('图标')
        .appendField(new Blockly.FieldDropdown(UI_LVGL_LIST_SYMBOL_OPTIONS as unknown as [string, string][]), 'SYMBOL');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本');
      this.setInputsInline(true);
      this.setOutput(true, getUiObjectCompatibleTypes());
      this.setColour(285);
      this.setTooltip('调用 lv_list_add_btn，返回创建的按钮对象。');
      this.setFieldValue('NULL', 'SYMBOL');
    },
  };

  Blockly.Blocks['yc_ui_list_get_btn_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('list')]).appendField('获取列表按钮文本 列表');
      this.appendValueInput('BTN').setCheck(getUiObjectCompatibleTypes()).appendField('按钮对象');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(285);
      this.setTooltip('调用 lv_list_get_btn_text。');
    },
  };

  Blockly.Blocks['cs_ui_screen_create'] = {
    init() {
      this.appendDummyInput()
        .appendField('简易UI 创建屏幕到')
        .appendField(new Blockly.FieldVariable('ui_screen_1', undefined, [getUiObjectType('screen')], getUiObjectType('screen')), 'OBJ_VAR');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void* ui_scr_create(void)');
    },
  };

  Blockly.Blocks['cs_ui_screen_show'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck([getUiObjectType('screen')])
        .appendField('简易UI 显示屏幕');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void ui_scr_show(void* scr)');
    },
  };

  Blockly.Blocks['cs_ui_lable_create'] = {
    init() {
      this.appendValueInput('PARENT')
        .setCheck(getUiSimpleCompatibleTypes())
        .appendField('简易UI 在父对象');
      this.appendDummyInput()
        .appendField('创建标签到')
        .appendField(new Blockly.FieldVariable('ui_label_1', undefined, [getUiObjectType('label')], getUiObjectType('label')), 'OBJ_VAR');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本');
      this.appendValueInput('X').setCheck('Number').appendField('X');
      this.appendValueInput('Y').setCheck('Number').appendField('Y');
      this.appendValueInput('W').setCheck('Number').appendField('宽');
      this.appendValueInput('H').setCheck('Number').appendField('高');
      this.setInputsInline(false);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void* ui_lbl_create(void* parent, char* text, int x, int y, int w, int h)');
    },
  };

  Blockly.Blocks['cs_ui_label_set_text'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('label')]).appendField('简易UI 设置标签');
      this.appendValueInput('TEXT').setCheck('String').appendField('文本为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void ui_set_text(void* lbl, char* text)');
    },
  };

  Blockly.Blocks['cs_ui_label_set_font'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('label')]).appendField('简易UI 设置标签');
      this.appendDummyInput()
        .appendField('字体')
        .appendField(new Blockly.FieldDropdown(UI_SIMPLE_FONT_OPTIONS as unknown as [string, string][]), 'FONT');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void ui_set_font(void* lbl, int font)');
      this.setFieldValue('FONT_MEDIUM', 'FONT');
    },
  };

  Blockly.Blocks['cs_ui_label_set_color'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('label')]).appendField('简易UI 设置标签');
      this.appendDummyInput()
        .appendField('颜色')
        .appendField(createUiSimpleColorPresetField(this), 'COLOR_PRESET')
        .appendField(new Blockly.FieldColour('#FFFFFF'), 'CUSTOM_COLOR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void ui_set_color(void* lbl, int color)');
    },
  };

  Blockly.Blocks['cs_ui_label_set_align'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('label')]).appendField('简易UI 设置标签');
      this.appendDummyInput()
        .appendField('对齐')
        .appendField(new Blockly.FieldDropdown(UI_SIMPLE_ALIGN_OPTIONS as unknown as [string, string][]), 'ALIGN');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void ui_set_align(void* lbl, int align)');
      this.setFieldValue('ALIGN_DEFAULT', 'ALIGN');
    },
  };

  Blockly.Blocks['cs_ui_set_xy'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck([getUiObjectType('label')]).appendField('简易UI 设置标签');
      this.appendValueInput('X').setCheck('Number').appendField('X');
      this.appendValueInput('Y').setCheck('Number').appendField('Y');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void ui_set_xy(void* lbl, int x, int y)');
    },
  };

  Blockly.Blocks['cs_ui_delete'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck(getUiSimpleCompatibleTypes())
        .appendField('简易UI 删除对象');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void ui_delete(void* obj)');
    },
  };

  Blockly.Blocks['cs_ui_set_background_color'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck(getUiSimpleCompatibleTypes())
        .appendField('简易UI 设置背景色 对象');
      this.appendDummyInput()
        .appendField('颜色')
        .appendField(createUiSimpleColorPresetField(this), 'COLOR_PRESET')
        .appendField(new Blockly.FieldColour('#FFFFFF'), 'CUSTOM_COLOR');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(285);
      this.setTooltip('void ui_set_bg_color(void* obj, int color)');
    },
  };

  Blockly.Blocks['ym_led_set_power'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-LED')
        .appendField('YM-LED');
      this.appendDummyInput()
        .appendField('设置LED开关为')
        .appendField(new Blockly.FieldDropdown([
          ['开', '1'],
          ['关', '0'],
        ]), 'STATE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_LED.POWER)}（uint8）：1=开，0=关；闪光灯模式下写入 1 会闪光一次。`);
    },
  };

  Blockly.Blocks['ym_led_set_brightness'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-LED')
        .appendField('YM-LED');
      this.appendValueInput('BRIGHTNESS')
        .setCheck('Number')
        .appendField('设置LED亮度为');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_LED.BRIGHTNESS)}（uint8）：范围 0-100。`);
    },
  };

  Blockly.Blocks['ym_led_flash_once'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-LED')
        .appendField('YM-LED');
      this.appendValueInput('DURATION')
        .setCheck('Number')
        .appendField('闪光一次，时长（毫秒）');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_LED.FLASH_ONCE)}（uint8）触发一次闪烁：时长单位为 10 毫秒，范围 10-2550 毫秒。`);
    },
  };

  Blockly.Blocks['ym_au_play_audio'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-AU')
        .appendField('YM-AU');
      this.appendDummyInput()
        .appendField('播放音频')
        .appendField(new Blockly.FieldDropdown([
          ['音频 1', '1'],
          ['音频 2', '2'],
          ['音频 3', '3'],
          ['音频 4', '4'],
          ['音频 5', '5'],
          ['音频 6', '6'],
          ['音频 7', '7'],
          ['音频 8', '8'],
        ]), 'AUDIO');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_AU.PLAY_AUDIO)}（uint8）：范围 1-8。`);
    },
  };

  Blockly.Blocks['ym_au_record_control'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-AU')
        .appendField('YM-AU');
      this.appendDummyInput()
        .appendField('录音控制')
        .appendField(new Blockly.FieldDropdown([
          ['停止', '0'],
          ['播放', '1'],
          ['录音', '2'],
        ]), 'CONTROL');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_AU.RECORD_CONTROL)}（uint8）：0=停止，1=播放，2=录音。`);
    },
  };

  Blockly.Blocks['ym_pir_read_status'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-PIR')
        .appendField('YM-PIR');
      this.appendDummyInput().appendField('读取人体状态');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_PIR.STATUS)}（uint8）：1=有人，0=无人。`);
    },
  };

  Blockly.Blocks['ym_pir_when_approach'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-PIR')
        .appendField('YM-PIR');
      this.appendDummyInput().appendField('当有人接近');
      this.appendStatementInput('DO').appendField('执行');
      this.setInputsInline(true);
      this.setColour(200);
      this.setTooltip('人体状态从 0->1 时触发。');
    },
  };

  Blockly.Blocks['ym_pir_when_leave'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-PIR')
        .appendField('YM-PIR');
      this.appendDummyInput().appendField('当有人离开');
      this.appendStatementInput('DO').appendField('执行');
      this.setInputsInline(true);
      this.setColour(200);
      this.setTooltip('人体状态从 1->0 时触发。');
    },
  };

  Blockly.Blocks['ym_dlgt_read_light'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-DLGT')
        .appendField('YM-DLGT');
      this.appendDummyInput().appendField('读取光照强度');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_DLGT.LIGHT)}（uint16，小端）。`);
    },
  };

  Blockly.Blocks['ym_beep_set_frequency'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-BEEP')
        .appendField('YM-BEEP');
      this.appendValueInput('FREQUENCY')
        .setCheck('Number')
        .appendField('设置频率(Hz)');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_BEEP.FREQUENCY)}（uint32，小端）。`);
    },
  };

  Blockly.Blocks['ym_beep_set_power'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-BEEP')
        .appendField('YM-BEEP');
      this.appendDummyInput()
        .appendField('设置蜂鸣器')
        .appendField(new Blockly.FieldDropdown([
          ['开', '1'],
          ['关', '0'],
        ]), 'STATE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_BEEP.POWER)}（uint8）：1=开，0=关。`);
    },
  };

  Blockly.Blocks['ym_ryg_set_light'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-RYG')
        .appendField('YM-RYG');
      this.appendDummyInput()
        .appendField('设置')
        .appendField(new Blockly.FieldDropdown([
          ['红灯', 'red'],
          ['黄灯', 'yellow'],
          ['绿灯', 'green'],
        ]), 'COLOR')
        .appendField('为')
        .appendField(new Blockly.FieldDropdown([
          ['开', '1'],
          ['关', '0'],
        ]), 'STATE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_RYG.LIGHT)}（uint8 位图）：bit0 红、bit1 黄、bit2 绿。`);
    },
  };

  Blockly.Blocks['ym_k3_read_status'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-K3')
        .appendField('YM-K3');
      this.appendDummyInput().appendField('读取按键状态');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_K3.STATUS)}（uint8）：bit0 黑、bit1 红、bit2 绿。`);
    },
  };

  Blockly.Blocks['ym_k3_when_black_pressed'] = {
    init() {
      this.appendDummyInput().appendField('YM-K3 当黑键按下');
      this.setInputsInline(true);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('黑键 bit0 从 0->1 时触发，并执行下方全部积木。');
    },
  };

  Blockly.Blocks['ym_k3_when_red_pressed'] = {
    init() {
      this.appendDummyInput().appendField('YM-K3 当红键按下');
      this.setInputsInline(true);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('红键 bit1 从 0->1 时触发，并执行下方全部积木。');
    },
  };

  Blockly.Blocks['ym_k3_when_green_pressed'] = {
    init() {
      this.appendDummyInput().appendField('YM-K3 当绿键按下');
      this.setInputsInline(true);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('绿键 bit2 从 0->1 时触发，并执行下方全部积木。');
    },
  };

  Blockly.Blocks['ym_swusb_set_power'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-SWUSB')
        .appendField('YM-SWUSB');
      this.appendDummyInput()
        .appendField('设置电源为')
        .appendField(new Blockly.FieldDropdown([
          ['开', '1'],
          ['关', '0'],
        ]), 'STATE');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_SWUSB.POWER)}（uint8）：1=开，0=关。`);
    },
  };

  Blockly.Blocks['ym_swusb_set_level'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-SWUSB')
        .appendField('YM-SWUSB');
      this.appendValueInput('LEVEL')
        .setCheck('Number')
        .appendField('设置功率(0-100)');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_SWUSB.LEVEL)}（uint8）：范围 0-100。`);
    },
  };

  Blockly.Blocks['ym_th1_read_temperature'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-TH1')
        .appendField('YM-TH1');
      this.appendDummyInput().appendField('读取温度(℃)');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_TH1.TEMPERATURE)}（float32，小端），单位 ℃。`);
    },
  };

  Blockly.Blocks['ym_th1_read_humidity'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-TH1')
        .appendField('YM-TH1');
      this.appendDummyInput().appendField('读取湿度(%)');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_TH1.HUMIDITY)}（uint8），范围 0-100。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_satellites'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取卫星数量');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.SATELLITES)}（uint8）：范围 0-54。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_beidou_satellites'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取北斗卫星数量');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.BEIDOU_SATELLITES)}（uint8）：范围 0-30。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_gps_satellites'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取GPS卫星数量');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.GPS_SATELLITES)}（uint8）：范围 0-24。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_speed'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取速度');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.SPEED)}（float32，小端）。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_angle'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取运动角度');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.ANGLE)}（char[8]）。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_acceleration'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取加速度');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.ACCELERATION)}（float32，小端）。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_time'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取卫星时间');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.TIME)}（char[18]，格式 yyyymmdd-hh:mm:ss）。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_position_status'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取定位状态');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.POSITION_STATUS)}（uint8）：1=已定位，0=未定位。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_altitude'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取海拔高度');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.ALTITUDE)}（char[16]，单位米）。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_latitude'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取纬度');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.LATITUDE)}（char[16]，6 位小数）。`);
    },
  };

  Blockly.Blocks['ym_gnss_read_longitude'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-GNSS')
        .appendField('YM-GNSS');
      this.appendDummyInput().appendField('读取经度');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_GNSS.LONGITUDE)}（char[16]，6 位小数）。`);
    },
  };

  Blockly.Blocks['ym_gnss_calculate_distance'] = {
    init() {
      this.appendValueInput('LAT1').setCheck('Number').appendField('计算 GNSS 距离 纬度');
      this.appendValueInput('LON1').setCheck('Number').appendField('经度');
      this.appendValueInput('LAT2').setCheck('Number').appendField('到 纬度');
      this.appendValueInput('LON2').setCheck('Number').appendField('经度');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip('计算两组经纬度的球面距离，单位为千米。');
    },
  };

  Blockly.Blocks['ym_us_read_distance'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-US')
        .appendField('YM-US');
      this.appendDummyInput().appendField('读取距离(mm)');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_US.DISTANCE)}（uint32，小端），单位 mm。`);
    },
  };

  Blockly.Blocks['ym_5g_read_signal_strength'] = {
    init() {
      this.appendValueInput('HANDLE')
        .setCheck('PeripheralHandle:YM-5G')
        .appendField('YM-5G');
      this.appendDummyInput().appendField('读取信号强度');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.SIGNAL_STRENGTH)}（uint8）：范围 0-31。`);
    },
  };

  Blockly.Blocks['ym_5g_read_imei'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取IMEI');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.IMEI)}（char[16]）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_operator'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取运营商');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.OPERATOR)}（char[5]）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_iccid'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取SIM卡号(ICCID)');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.ICCID)}（char[20]）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_sim_id'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取SIM_ID');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.SIM_ID)}（uint8）：0=外置SIM，1=eSIM。`);
    },
  };

  Blockly.Blocks['ym_5g_set_sim_id'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput()
        .appendField('设置SIM_ID为')
        .appendField(new Blockly.FieldDropdown([
          ['外置SIM(0)', '0'],
          ['eSIM(1)', '1'],
          ['自动(2)', '2'],
        ]), 'SIM_ID');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.SIM_ID)}（uint8）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_data'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取接收数据');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.DATA)}（uint8[20]，调试数据）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_state'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取模块状态');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.STATE)}（uint8）：1=正常，0=连接失败。`);
    },
  };

  Blockly.Blocks['ym_5g_read_mqtt_connect'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取MQTT连接开关');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.MQTT_CONNECT)}（uint8）。`);
    },
  };

  Blockly.Blocks['ym_5g_mqtt_connect'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('连接MQTT');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip('调用 ym_mqtt_connect 连接 MQTT 服务器。');
    },
  };

  Blockly.Blocks['ym_5g_mqtt_disconnect'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('断开MQTT');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip('调用 ym_mqtt_disconnect 断开 MQTT 服务器。');
    },
  };

  Blockly.Blocks['ym_5g_read_user'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取用户名');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.USER)}（char[20]）。`);
    },
  };

  Blockly.Blocks['ym_5g_set_user'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendValueInput('USER').setCheck('String').appendField('设置用户名');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.USER)}（char[20]）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_password'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取密码');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.PASSWORD)}（char[20]）。`);
    },
  };

  Blockly.Blocks['ym_5g_set_password'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendValueInput('PASSWORD').setCheck('String').appendField('设置密码');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.PASSWORD)}（char[20]）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_qos'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取QoS');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.QOS)}（uint8）。`);
    },
  };

  Blockly.Blocks['ym_5g_set_qos'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendValueInput('QOS').setCheck('Number').appendField('设置QoS');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.QOS)}（uint8）：0/1/2。`);
    },
  };

  Blockly.Blocks['ym_5g_read_topic'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取主题');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.TOPIC)}（char[64]）。`);
    },
  };

  Blockly.Blocks['ym_5g_mqtt_subscribe'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendValueInput('TOPIC').setCheck('String').appendField('订阅主题');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip('调用 ym_mqtt_subscribe 订阅 MQTT 主题。');
    },
  };

  Blockly.Blocks['ym_5g_read_client_id'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取客户端ID');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.CLIENT_ID)}（char[64]）。`);
    },
  };

  Blockly.Blocks['ym_5g_set_client_id'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendValueInput('CLIENT_ID').setCheck('String').appendField('设置客户端ID');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.CLIENT_ID)}（char[64]）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_host'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取服务器地址');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.HOST)}（char[128]）。`);
    },
  };

  Blockly.Blocks['ym_5g_set_host'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendValueInput('HOST').setCheck('String').appendField('设置服务器地址');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.HOST)}（char[128]）。`);
    },
  };

  Blockly.Blocks['ym_5g_read_port'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取端口号');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.PORT)}（uint16，小端）。`);
    },
  };

  Blockly.Blocks['ym_5g_set_port'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendValueInput('PORT').setCheck('Number').appendField('设置端口号');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`写入特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.PORT)}（uint16）。`);
    },
  };

  Blockly.Blocks['ym_5g_set_mqtt_config'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G').appendField('MQTT');
      this.appendValueInput('CLIENT_ID').setCheck('String').appendField('客户端ID');
      this.appendValueInput('HOST').setCheck('String').appendField('服务器地址');
      this.appendValueInput('PORT').setCheck('Number').appendField('服务器端口');
      this.appendValueInput('USER').setCheck('String').appendField('用户名');
      this.appendValueInput('PASSWORD').setCheck('String').appendField('密码');
      this.setInputsInline(false);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip(`分组设置 MQTT 参数：${toHexUuid(YM_XXX_UUID.YM_5G.CLIENT_ID)} / ${toHexUuid(YM_XXX_UUID.YM_5G.HOST)} / ${toHexUuid(YM_XXX_UUID.YM_5G.PORT)} / ${toHexUuid(YM_XXX_UUID.YM_5G.USER)} / ${toHexUuid(YM_XXX_UUID.YM_5G.PASSWORD)}。`);
    },
  };

  Blockly.Blocks['ym_5g_mqtt_publish'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendValueInput('TOPIC').setCheck('String').appendField('向主题');
      this.appendValueInput('MESSAGE').setCheck('String').appendField('发送MQTT消息');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(200);
      this.setTooltip('调用 ym_mqtt_publish 向指定 MQTT 主题发送消息。');
    },
  };

  Blockly.Blocks['ym_5g_read_subscribe'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取订阅消息');
      this.setInputsInline(true);
      this.setOutput(true, 'String');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.SUBSCRIBE)}（char[256]）。`);
    },
  };

  Blockly.Blocks['ym_5g_on_subscribe_message'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('当收到MQTT消息');
      this.appendDummyInput()
        .appendField('消息变量')
        .appendField(new Blockly.FieldVariable('mqtt_msg'), 'PAYLOAD_VAR');
      this.setInputsInline(true);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip(`监听特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.SUBSCRIBE)}（UTF-8 文本）变化并触发。`);
    },
  };

  Blockly.Blocks['ym_5g_read_mqtt_state'] = {
    init() {
      this.appendValueInput('HANDLE').setCheck('PeripheralHandle:YM-5G').appendField('YM-5G');
      this.appendDummyInput().appendField('读取MQTT连接状态');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setColour(200);
      this.setTooltip(`读取特征值 ${toHexUuid(YM_XXX_UUID.YM_5G.MQTT_STATE)}（uint8）：0未连接，1已连接。`);
    },
  };

  Object.entries(Blockly.Blocks).forEach(([blockType, definition]) => {
    if (!blockType.startsWith('ym_') || !definition?.init) return;

    const originalInit = definition.init;
    definition.init = function () {
      originalInit.call(this);
      const handleInput = this.getInput('HANDLE');
      const handleChecks: string[] = handleInput?.connection?.getCheck() ?? [];
      const objectChecks = handleChecks
        .filter((check) => check.startsWith('PeripheralHandle:'))
        .map((check) => check.replace(/^PeripheralHandle:/, 'PeripheralObject:'));
      if (handleInput && objectChecks.length > 0) {
        handleInput.setCheck(Array.from(new Set([...handleChecks, ...objectChecks])));
      }
    };
  });

  // ============ 3D Blocks ============
  Blockly.Blocks['three_create_object'] = {
    init() {
      this.appendDummyInput()
        .appendField('创建 3D 对象')
        .appendField(new Blockly.FieldDropdown([
          ['立方体', 'cube'],
          ['球体', 'sphere'],
        ]), 'SHAPE');
      this.setOutput(true, 'Object3D');
      this.setColour(280);
      this.setTooltip('创建 3D 对象');
    },
  };

  Blockly.Blocks['three_transform'] = {
    init() {
      this.appendValueInput('TARGET')
        .setCheck('Object3D')
        .appendField('变换对象');
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown([
          ['移动', 'move'],
          ['旋转', 'rotate'],
          ['缩放', 'scale'],
        ]), 'MODE');
      this.appendValueInput('X')
        .setCheck('Number')
        .appendField('X');
      this.appendValueInput('Y')
        .setCheck('Number')
        .appendField('Y');
      this.appendValueInput('Z')
        .setCheck('Number')
        .appendField('Z');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(280);
      this.setTooltip('移动 3D 对象');
    },
  };

  Blockly.Blocks['three_on_key'] = {
    init() {
      this.appendDummyInput()
        .appendField('当按下按键')
        .appendField(new Blockly.FieldDropdown([
          ['↑', 'ArrowUp'],
          ['↓', 'ArrowDown'],
          ['←', 'ArrowLeft'],
          ['→', 'ArrowRight'],
          ['空格', 'Space'],
        ]), 'KEY');
      this.appendStatementInput('DO').appendField('执行');
      this.setColour(280);
      this.setTooltip('按键触发事件');
    },
  };

  Blockly.Blocks['three_on_collision'] = {
    init() {
      this.appendValueInput('TARGET')
        .setCheck('Object3D')
        .appendField('当对象');
      this.appendDummyInput().appendField('发生碰撞');
      this.appendStatementInput('DO').appendField('执行');
      this.setColour(280);
      this.setTooltip('碰撞触发事件（占位）');
    },
  };

  // ============ Scratch 舞台 Blocks =========
  Blockly.Blocks['scratch_stage_switch_backdrop'] = {
    init() {
      this.appendValueInput('BACKDROP')
        .setCheck('String')
        .appendField('切换舞台背景为');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('切换舞台背景');
    },
  };

  Blockly.Blocks['scratch_stage_next_backdrop'] = {
    init() {
      this.appendDummyInput().appendField('下一个舞台背景');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('切换到下一个舞台背景');
    },
  };

  Blockly.Blocks['scratch_stage_set_mode'] = {
    init() {
      this.appendDummyInput()
        .appendField('舞台模式')
        .appendField(new Blockly.FieldDropdown([
          ['平面', 'flat'],
          ['三维', '3d'],
        ]), 'MODE');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('切换舞台模式');
    },
  };

  Blockly.Blocks['scratch_stage_show'] = {
    init() {
      this.appendDummyInput().appendField('显示舞台');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('显示舞台');
    },
  };

  Blockly.Blocks['scratch_stage_hide'] = {
    init() {
      this.appendDummyInput().appendField('隐藏舞台');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('隐藏舞台');
    },
  };

  // ============ Scratch 控制 Blocks =========
  Blockly.Blocks['scratch_control_system_beep'] = {
    init() {
      this.appendValueInput('DURATION')
        .setCheck('Number')
        .appendField('系统蜂鸣器 响')
        .appendField('毫秒');
      this.appendValueInput('FREQ')
        .setCheck('Number')
        .appendField('频率');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(30);
      this.setTooltip('使用系统蜂鸣器发声');
    },
  };

  Blockly.Blocks['scratch_control_wait'] = {
    init() {
      this.appendValueInput('SECS')
        .setCheck('Number')
        .appendField('等待')
        .appendField('秒');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(30);
      this.setTooltip('等待指定秒数');
    },
  };

  Blockly.Blocks['scratch_control_wait_until'] = {
    init() {
      this.appendValueInput('CONDITION')
        .setCheck('Boolean')
        .appendField('等待直到');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(30);
      this.setTooltip('等待条件满足');
    },
  };

  Blockly.Blocks['scratch_control_repeat'] = {
    init() {
      this.appendValueInput('TIMES')
        .setCheck('Number')
        .appendField('重复');
      this.appendStatementInput('DO')
        .appendField('执行');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(30);
      this.setTooltip('重复执行');
    },
  };

  Blockly.Blocks['scratch_control_forever'] = {
    init() {
      this.appendStatementInput('DO')
        .appendField('重复执行');
      this.setPreviousStatement(true, null);
      this.setColour(30);
      this.setTooltip('一直重复执行');
    },
  };

  Blockly.Blocks['scratch_control_repeat_until'] = {
    init() {
      this.appendValueInput('CONDITION')
        .setCheck('Boolean')
        .appendField('重复直到');
      this.appendStatementInput('DO')
        .appendField('执行');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(30);
      this.setTooltip('重复直到条件满足');
    },
  };

  Blockly.Blocks['scratch_control_stop'] = {
    init() {
      this.appendDummyInput()
        .appendField('停止')
        .appendField(
          new Blockly.FieldDropdown([
            ['全部', 'all'],
            ['此脚本', 'this'],
            ['其他脚本', 'other'],
          ]),
          'SCOPE'
        );
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(30);
      this.setTooltip('停止脚本');
    },
  };

  Blockly.Blocks['scratch_control_create_clone_of'] = {
    init() {
      this.appendValueInput('TARGET')
        .setCheck('String')
        .appendField('创建克隆');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(30);
      this.setTooltip('创建克隆');
    },
  };

  Blockly.Blocks['scratch_control_delete_clone'] = {
    init() {
      this.appendDummyInput().appendField('删除此克隆体');
      this.setPreviousStatement(true, null);
      this.setColour(30);
      this.setTooltip('删除当前克隆体');
    },
  };

  // ============ Scratch 运动/外观/声音/事件/侦测 Blocks =========
  Blockly.Blocks['scratch_motion_move_steps'] = {
    init() {
      this.appendValueInput('STEPS')
        .setCheck('Number')
        .appendField('移动');
      this.appendDummyInput().appendField('步');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('移动指定步数');
    },
  };

  Blockly.Blocks['scratch_motion_turn_right'] = {
    init() {
      this.appendValueInput('DEGREES')
        .setCheck('Number')
        .appendField('右转');
      this.appendDummyInput().appendField('度');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('顺时针旋转');
    },
  };

  Blockly.Blocks['scratch_motion_turn_left'] = {
    init() {
      this.appendValueInput('DEGREES')
        .setCheck('Number')
        .appendField('左转');
      this.appendDummyInput().appendField('度');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('逆时针旋转');
    },
  };

  Blockly.Blocks['scratch_motion_go_to_xy'] = {
    init() {
      this.appendValueInput('X')
        .setCheck('Number')
        .appendField('移到 x');
      this.appendValueInput('Y')
        .setCheck('Number')
        .appendField('y');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('移动到指定坐标');
    },
  };

  Blockly.Blocks['scratch_motion_glide_to_xy'] = {
    init() {
      this.appendValueInput('SECS')
        .setCheck('Number')
        .appendField('在');
      this.appendDummyInput().appendField('秒内滑行到');
      this.appendValueInput('X')
        .setCheck('Number')
        .appendField('x');
      this.appendValueInput('Y')
        .setCheck('Number')
        .appendField('y');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('滑行到指定坐标');
    },
  };

  Blockly.Blocks['scratch_motion_point_in_direction'] = {
    init() {
      this.appendValueInput('DIRECTION')
        .setCheck('Number')
        .appendField('面向方向');
      this.appendDummyInput().appendField('度');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('设置方向');
    },
  };

  Blockly.Blocks['scratch_motion_set_x'] = {
    init() {
      this.appendValueInput('X')
        .setCheck('Number')
        .appendField('将 x 设为');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('设置 x 坐标');
    },
  };

  Blockly.Blocks['scratch_motion_set_y'] = {
    init() {
      this.appendValueInput('Y')
        .setCheck('Number')
        .appendField('将 y 设为');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('设置 y 坐标');
    },
  };

  Blockly.Blocks['scratch_motion_change_x'] = {
    init() {
      this.appendValueInput('DX')
        .setCheck('Number')
        .appendField('将 x 增加');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('改变 x 坐标');
    },
  };

  Blockly.Blocks['scratch_motion_change_y'] = {
    init() {
      this.appendValueInput('DY')
        .setCheck('Number')
        .appendField('将 y 增加');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('改变 y 坐标');
    },
  };

  Blockly.Blocks['scratch_motion_if_on_edge_bounce'] = {
    init() {
      this.appendDummyInput().appendField('碰到边缘就反弹');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(210);
      this.setTooltip('碰到边缘反弹');
    },
  };

  Blockly.Blocks['scratch_motion_x_position'] = {
    init() {
      this.appendDummyInput().appendField('x 坐标');
      this.setOutput(true, 'Number');
      this.setColour(210);
      this.setTooltip('当前 x 坐标');
    },
  };

  Blockly.Blocks['scratch_motion_y_position'] = {
    init() {
      this.appendDummyInput().appendField('y 坐标');
      this.setOutput(true, 'Number');
      this.setColour(210);
      this.setTooltip('当前 y 坐标');
    },
  };

  Blockly.Blocks['scratch_motion_direction'] = {
    init() {
      this.appendDummyInput().appendField('方向');
      this.setOutput(true, 'Number');
      this.setColour(210);
      this.setTooltip('当前方向');
    },
  };

  Blockly.Blocks['scratch_stage_set_speech_voice'] = {
    init() {
      this.appendDummyInput()
        .appendField('设置语音合成音色')
        .appendField(new Blockly.FieldDropdown([
          ['后台默认', ''],
          ['Cherry', 'Cherry'],
          ['Ethan', 'Ethan'],
          ['Chelsie', 'Chelsie'],
          ['Serena', 'Serena'],
          ['Dylan', 'Dylan'],
          ['Jada', 'Jada'],
          ['Sunny', 'Sunny'],
        ]), 'VOICE');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(30);
      this.setTooltip('设置舞台说话时使用的语音合成音色');
    },
  };

  Blockly.Blocks['scratch_looks_say'] = {
    init() {
      this.appendValueInput('MESSAGE')
        .setCheck('String')
        .appendField('说');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('说一段话');
    },
  };

  Blockly.Blocks['scratch_looks_say_for_secs'] = {
    init() {
      this.appendValueInput('MESSAGE')
        .setCheck('String')
        .appendField('说');
      this.appendValueInput('SECS')
        .setCheck('Number')
        .appendField('持续');
      this.appendDummyInput().appendField('秒');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('说指定时间');
    },
  };

  Blockly.Blocks['scratch_looks_think'] = {
    init() {
      this.appendValueInput('MESSAGE')
        .setCheck('String')
        .appendField('想');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('思考一段话');
    },
  };

  Blockly.Blocks['scratch_looks_think_for_secs'] = {
    init() {
      this.appendValueInput('MESSAGE')
        .setCheck('String')
        .appendField('想');
      this.appendValueInput('SECS')
        .setCheck('Number')
        .appendField('持续');
      this.appendDummyInput().appendField('秒');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('思考指定时间');
    },
  };

  Blockly.Blocks['scratch_looks_show'] = {
    init() {
      this.appendDummyInput().appendField('显示角色');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('显示角色');
    },
  };

  Blockly.Blocks['scratch_looks_hide'] = {
    init() {
      this.appendDummyInput().appendField('隐藏角色');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('隐藏角色');
    },
  };

  Blockly.Blocks['scratch_looks_switch_costume'] = {
    init() {
      this.appendValueInput('COSTUME')
        .setCheck('String')
        .appendField('切换造型为');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('切换造型');
    },
  };

  Blockly.Blocks['scratch_looks_next_costume'] = {
    init() {
      this.appendDummyInput().appendField('下一个造型');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('切换到下一个造型');
    },
  };

  Blockly.Blocks['scratch_looks_change_size'] = {
    init() {
      this.appendValueInput('SIZE')
        .setCheck('Number')
        .appendField('将大小增加');
      this.appendDummyInput().appendField('%');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('改变大小');
    },
  };

  Blockly.Blocks['scratch_looks_set_size'] = {
    init() {
      this.appendValueInput('SIZE')
        .setCheck('Number')
        .appendField('将大小设为');
      this.appendDummyInput().appendField('%');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(300);
      this.setTooltip('设置大小');
    },
  };

  Blockly.Blocks['scratch_looks_size'] = {
    init() {
      this.appendDummyInput().appendField('大小');
      this.setOutput(true, 'Number');
      this.setColour(300);
      this.setTooltip('当前大小');
    },
  };

  Blockly.Blocks['scratch_sound_play'] = {
    init() {
      this.appendValueInput('SOUND')
        .setCheck('String')
        .appendField('播放声音');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(330);
      this.setTooltip('播放声音');
    },
  };

  Blockly.Blocks['scratch_sound_play_until_done'] = {
    init() {
      this.appendValueInput('SOUND')
        .setCheck('String')
        .appendField('播放声音直到播放完毕');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(330);
      this.setTooltip('播放声音直到结束');
    },
  };

  Blockly.Blocks['scratch_sound_stop_all'] = {
    init() {
      this.appendDummyInput().appendField('停止所有声音');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(330);
      this.setTooltip('停止所有声音');
    },
  };

  Blockly.Blocks['scratch_sound_change_volume'] = {
    init() {
      this.appendValueInput('VOLUME')
        .setCheck('Number')
        .appendField('将音量增加');
      this.appendDummyInput().appendField('%');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(330);
      this.setTooltip('改变音量');
    },
  };

  Blockly.Blocks['scratch_sound_set_volume'] = {
    init() {
      this.appendValueInput('VOLUME')
        .setCheck('Number')
        .appendField('将音量设为');
      this.appendDummyInput().appendField('%');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(330);
      this.setTooltip('设置音量');
    },
  };

  Blockly.Blocks['scratch_sound_volume'] = {
    init() {
      this.appendDummyInput().appendField('音量');
      this.setOutput(true, 'Number');
      this.setColour(330);
      this.setTooltip('当前音量');
    },
  };

  Blockly.Blocks['scratch_event_when_green_flag'] = {
    init() {
      this.appendDummyInput()
        .appendField(new Blockly.FieldImage(
          'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22%3E%3Cpath fill=%22%2328a745%22 d=%22M4 2h2v20H4zM6 4c5-3 8 3 14 0v10c-6 3-9-3-14 0z%22/%3E%3C/svg%3E',
          18,
          18,
          '绿旗'
        ))
        .appendField('当绿旗被点击');
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('点击此积木执行下方全部积木');
    },
  };

  Blockly.Blocks['scratch_event_when_key_pressed'] = {
    init() {
      this.appendDummyInput()
        .appendField('当按下按键')
        .appendField(new Blockly.FieldDropdown([
          ['↑', 'ArrowUp'],
          ['↓', 'ArrowDown'],
          ['←', 'ArrowLeft'],
          ['→', 'ArrowRight'],
          ['空格', 'Space'],
          ['任意键', 'any'],
        ]), 'KEY');
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('按键后执行下方全部积木');
    },
  };

  Blockly.Blocks['scratch_event_when_sprite_clicked'] = {
    init() {
      this.appendDummyInput().appendField('当角色被点击');
      this.setNextStatement(true, null);
      this.setColour(45);
      this.setTooltip('运行时点击当前角色后执行下方全部积木');
    },
  };

  Blockly.Blocks['scratch_event_broadcast'] = {
    init() {
      this.appendValueInput('MESSAGE')
        .setCheck('String')
        .appendField('广播消息');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(60);
      this.setTooltip('广播消息');
    },
  };

  Blockly.Blocks['scratch_event_broadcast_and_wait'] = {
    init() {
      this.appendValueInput('MESSAGE')
        .setCheck('String')
        .appendField('广播消息并等待');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(60);
      this.setTooltip('广播消息并等待');
    },
  };

  Blockly.Blocks['scratch_event_when_receive'] = {
    init() {
      this.appendValueInput('MESSAGE')
        .setCheck('String')
        .appendField('当收到广播');
      this.appendStatementInput('DO').appendField('执行');
      this.setNextStatement(true, null);
      this.setColour(60);
      this.setTooltip('收到广播');
    },
  };

  Blockly.Blocks['scratch_sensing_touching'] = {
    init() {
      this.appendDummyInput()
        .appendField('碰到')
        .appendField(new Blockly.FieldDropdown([
          ['鼠标指针', 'mouse'],
          ['舞台边缘', 'edge'],
          ['角色1', 'sprite1'],
        ]), 'TARGET');
      this.setOutput(true, 'Boolean');
      this.setColour(180);
      this.setTooltip('是否碰到目标');
    },
  };

  Blockly.Blocks['scratch_sensing_touching_color'] = {
    init() {
      this.appendDummyInput()
        .appendField('碰到颜色')
        .appendField(new Blockly.FieldColour('#ff0000'), 'COLOR');
      this.setOutput(true, 'Boolean');
      this.setColour(180);
      this.setTooltip('是否碰到颜色');
    },
  };

  Blockly.Blocks['scratch_sensing_color_touching_color'] = {
    init() {
      this.appendDummyInput()
        .appendField('颜色')
        .appendField(new Blockly.FieldColour('#ff0000'), 'COLOR1')
        .appendField('碰到颜色')
        .appendField(new Blockly.FieldColour('#0000ff'), 'COLOR2');
      this.setOutput(true, 'Boolean');
      this.setColour(180);
      this.setTooltip('颜色是否碰到颜色');
    },
  };

  Blockly.Blocks['scratch_sensing_distance_to'] = {
    init() {
      this.appendDummyInput()
        .appendField('到')
        .appendField(new Blockly.FieldDropdown([
          ['鼠标指针', 'mouse'],
          ['角色1', 'sprite1'],
        ]), 'TARGET')
        .appendField('的距离');
      this.setOutput(true, 'Number');
      this.setColour(180);
      this.setTooltip('与目标的距离');
    },
  };

  Blockly.Blocks['scratch_sensing_ask_and_wait'] = {
    init() {
      this.appendValueInput('QUESTION')
        .setCheck('String')
        .appendField('询问并等待');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(180);
      this.setTooltip('询问问题');
    },
  };

  Blockly.Blocks['scratch_sensing_answer'] = {
    init() {
      this.appendDummyInput().appendField('回答');
      this.setOutput(true, 'String');
      this.setColour(180);
      this.setTooltip('最近一次回答');
    },
  };

  Blockly.Blocks['scratch_sensing_key_pressed'] = {
    init() {
      this.appendDummyInput()
        .appendField('按键')
        .appendField(new Blockly.FieldDropdown([
          ['↑', 'ArrowUp'],
          ['↓', 'ArrowDown'],
          ['←', 'ArrowLeft'],
          ['→', 'ArrowRight'],
          ['空格', 'Space'],
          ['任意键', 'any'],
        ]), 'KEY')
        .appendField('是否按下');
      this.setOutput(true, 'Boolean');
      this.setColour(180);
      this.setTooltip('按键是否按下');
    },
  };

  Blockly.Blocks['scratch_sensing_mouse_down'] = {
    init() {
      this.appendDummyInput().appendField('鼠标按下？');
      this.setOutput(true, 'Boolean');
      this.setColour(180);
      this.setTooltip('鼠标是否按下');
    },
  };

  Blockly.Blocks['scratch_sensing_mouse_x'] = {
    init() {
      this.appendDummyInput().appendField('鼠标 x 坐标');
      this.setOutput(true, 'Number');
      this.setColour(180);
      this.setTooltip('鼠标 x 坐标');
    },
  };

  Blockly.Blocks['scratch_sensing_mouse_y'] = {
    init() {
      this.appendDummyInput().appendField('鼠标 y 坐标');
      this.setOutput(true, 'Number');
      this.setColour(180);
      this.setTooltip('鼠标 y 坐标');
    },
  };

  Blockly.Blocks['scratch_sensing_loudness'] = {
    init() {
      this.appendDummyInput().appendField('响度');
      this.setOutput(true, 'Number');
      this.setColour(180);
      this.setTooltip('麦克风响度');
    },
  };

  Blockly.Blocks['scratch_sensing_timer'] = {
    init() {
      this.appendDummyInput().appendField('计时器');
      this.setOutput(true, 'Number');
      this.setColour(180);
      this.setTooltip('计时器值');
    },
  };

  Blockly.Blocks['scratch_sensing_reset_timer'] = {
    init() {
      this.appendDummyInput().appendField('计时器归零');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(180);
      this.setTooltip('重置计时器');
    },
  };

  Blockly.Blocks['scratch_sensing_current_time'] = {
    init() {
      this.appendDummyInput()
        .appendField('当前时间')
        .appendField(new Blockly.FieldDropdown([
          ['年', 'year'],
          ['月', 'month'],
          ['日', 'date'],
          ['小时', 'hour'],
          ['分钟', 'minute'],
          ['秒', 'second'],
        ]), 'UNIT');
      this.setOutput(true, 'Number');
      this.setColour(180);
      this.setTooltip('当前时间');
    },
  };

  Blockly.Blocks['scratch_sensing_days_since_2000'] = {
    init() {
      this.appendDummyInput().appendField('自 2000 年以来的天数');
      this.setOutput(true, 'Number');
      this.setColour(180);
      this.setTooltip('天数');
    },
  };

  const jsGenerator = javascriptGenerator as unknown as Blockly.Generator & {
    forBlock: Record<string, (block: Blockly.Block, generator: Blockly.Generator) => string | [string, number]>;
    ORDER_NONE: number;
    ORDER_FUNCTION_CALL: number;
  };
  const pyGenerator = pythonGenerator as unknown as Blockly.Generator & {
    forBlock: Record<string, (block: Blockly.Block, generator: Blockly.Generator) => string | [string, number]>;
    ORDER_NONE: number;
    ORDER_FUNCTION_CALL: number;
  };

  registerScratchExtensionBlocks();
  registerScratchExtensionGenerators(jsGenerator);

  const getVariableSymbolName = (
    block: Blockly.Block,
    fieldName: string,
    generator: Blockly.Generator,
    fallback: string
  ) => {
    const field = block.getField(fieldName) as Blockly.FieldVariable | null;
    const variable = field?.getVariable();
    const rawName = variable?.name || field?.getText() || block.getFieldValue(fieldName) || fallback;
    const nameDb = (generator as Blockly.Generator & {
      nameDB_?: { getName: (name: string, type: string) => string };
    }).nameDB_;
    if (nameDb && rawName) {
      return nameDb.getName(rawName, 'VARIABLE');
    }
    return rawName.replace(/[^A-Za-z0-9_]/g, '_');
  };

  type JsonGeneratorArgConfig = {
    name: string;
    fallback: string;
    source: 'field' | 'input';
  };

  const jsonInputArg = (name: string, fallback: string): JsonGeneratorArgConfig => ({
    name,
    fallback,
    source: 'input',
  });

  const jsonFieldArg = (name: string, fallback: string): JsonGeneratorArgConfig => ({
    name,
    fallback,
    source: 'field',
  });

  const jsonOutputGeneratorConfigs: Array<{ type: string; args: JsonGeneratorArgConfig[] }> = [
    { type: 'json_parse', args: [jsonInputArg('TEXT', '"{}"')] },
    { type: 'json_create_object', args: [] },
    { type: 'json_create_array', args: [] },
    {
      type: 'json_object_get',
      args: [
        jsonFieldArg('OBJ', 'json_object_1'),
        jsonInputArg('KEY', '"name"'),
      ],
    },
    { type: 'json_array_size', args: [jsonFieldArg('ARR', 'json_array_1')] },
    {
      type: 'json_array_get',
      args: [
        jsonFieldArg('ARR', 'json_array_1'),
        jsonInputArg('INDEX', '0'),
      ],
    },
    { type: 'json_as_string', args: [jsonFieldArg('JSON', 'json_node_1')] },
    { type: 'json_as_number', args: [jsonFieldArg('JSON', 'json_node_1')] },
    { type: 'json_as_bool', args: [jsonFieldArg('JSON', 'json_node_1')] },
  ];
  const jsonStatementGeneratorConfigs: Array<{ type: string; args: JsonGeneratorArgConfig[] }> = [
    { type: 'json_delete', args: [jsonFieldArg('JSON', 'json_node_1')] },
    {
      type: 'json_object_set_string',
      args: [
        jsonFieldArg('OBJ', 'json_object_1'),
        jsonInputArg('KEY', '"name"'),
        jsonInputArg('VALUE', '"ycais"'),
      ],
    },
    {
      type: 'json_object_set_number',
      args: [
        jsonFieldArg('OBJ', 'json_object_1'),
        jsonInputArg('KEY', '"temperature"'),
        jsonInputArg('VALUE', '25.5'),
      ],
    },
    {
      type: 'json_object_set_bool',
      args: [
        jsonFieldArg('OBJ', 'json_object_1'),
        jsonInputArg('KEY', '"online"'),
        jsonInputArg('VALUE', '1'),
      ],
    },
    {
      type: 'json_object_set_null',
      args: [
        jsonFieldArg('OBJ', 'json_object_1'),
        jsonInputArg('KEY', '"error"'),
      ],
    },
    {
      type: 'json_object_set_json',
      args: [
        jsonFieldArg('OBJ', 'json_object_1'),
        jsonInputArg('KEY', '"data"'),
        jsonFieldArg('CHILD', 'json_node_1'),
      ],
    },
    {
      type: 'json_array_add_string',
      args: [
        jsonFieldArg('ARR', 'json_array_1'),
        jsonInputArg('VALUE', '"hello"'),
      ],
    },
    {
      type: 'json_array_add_number',
      args: [
        jsonFieldArg('ARR', 'json_array_1'),
        jsonInputArg('VALUE', '123'),
      ],
    },
    {
      type: 'json_array_add_bool',
      args: [
        jsonFieldArg('ARR', 'json_array_1'),
        jsonInputArg('VALUE', '1'),
      ],
    },
    { type: 'json_array_add_null', args: [jsonFieldArg('ARR', 'json_array_1')] },
    {
      type: 'json_array_add_json',
      args: [
        jsonFieldArg('ARR', 'json_array_1'),
        jsonFieldArg('CHILD', 'json_node_1'),
      ],
    },
    {
      type: 'json_array_remove',
      args: [
        jsonFieldArg('ARR', 'json_array_1'),
        jsonInputArg('INDEX', '0'),
      ],
    },
  ];

  const getGeneratorCallArgs = (
    block: Blockly.Block,
    generator: Blockly.Generator,
    args: JsonGeneratorArgConfig[],
    order: number
  ) => args.map(({ name, fallback, source }) => {
    if (source === 'field') {
      return getJsonVariableSymbolName(block, name, generator, fallback);
    }
    return generator.valueToCode(block, name, order) || fallback;
  });

  const getJsonVariableSymbolName = (
    block: Blockly.Block,
    fieldName: string,
    generator: Blockly.Generator,
    fallback: string
  ) => getVariableSymbolName(block, fieldName, generator, fallback).trim() || fallback;

  // ============ JavaScript Generators ============
  jsGenerator.forBlock['text_print'] = function (block, generator) {
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return `print(${text});\n`;
  };

  jsGenerator.forBlock['text_number_to_string_custom'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '0';
    const digits = block.getFieldValue('DIGITS') || '6';
    return [
      `(await (async () => { const raw = ${value}; const n = Number(raw); const d = Math.max(0, Math.trunc(Number(${digits}) || 0)); return Number.isFinite(n) ? n.toFixed(d) : String(raw); })())`,
      jsGenerator.ORDER_FUNCTION_CALL,
    ];
  };

  jsGenerator.forBlock['text_concat_two_custom'] = function (block, generator) {
    const left = generator.valueToCode(block, 'LEFT', jsGenerator.ORDER_NONE) || '""';
    const right = generator.valueToCode(block, 'RIGHT', jsGenerator.ORDER_NONE) || '""';
    return [`(String(${left}) + String(${right}))`, jsGenerator.ORDER_NONE];
  };

  jsGenerator.forBlock['json_parse_text'] = function (block, generator) {
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '"{}"';
    return [
      `(() => { try { return JSON.parse(String(${text})); } catch { return {}; } })()`,
      jsGenerator.ORDER_FUNCTION_CALL,
    ];
  };

  jsGenerator.forBlock['json_stringify_object'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '{}';
    return [
      `(() => { try { const serialized = JSON.stringify(${value}); return serialized === undefined ? "null" : serialized; } catch { return "{}"; } })()`,
      jsGenerator.ORDER_FUNCTION_CALL,
    ];
  };

  jsGenerator.forBlock['yios_int2str'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '0';
    return [`String(Math.trunc(Number(${value}) || 0))`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yios_str2int'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '"0"';
    return [
      `(await (async () => { const raw = ${value}; const n = parseInt(String(raw), 10); return Number.isFinite(n) ? n : 0; })())`,
      jsGenerator.ORDER_FUNCTION_CALL,
    ];
  };

  jsGenerator.forBlock['yios_str2float'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '"0"';
    return [
      `(await (async () => { const raw = ${value}; const n = parseFloat(String(raw)); return Number.isFinite(n) ? n : 0; })())`,
      jsGenerator.ORDER_FUNCTION_CALL,
    ];
  };

  jsGenerator.forBlock['yios_float2str'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '0';
    return [
      `(await (async () => { const raw = ${value}; const n = Number(raw); return Number.isFinite(n) ? String(n) : String(raw); })())`,
      jsGenerator.ORDER_FUNCTION_CALL,
    ];
  };

  jsGenerator.forBlock['iot_wifi_enable'] = function () {
    return 'wifi_enable();\n';
  };

  jsGenerator.forBlock['iot_wifi_set_ssid_password'] = function (block, generator) {
    const ssid = generator.valueToCode(block, 'SSID', jsGenerator.ORDER_NONE) || '"my-ssid"';
    const password = generator.valueToCode(block, 'PASSWORD', jsGenerator.ORDER_NONE) || '"my-password"';
    return `wifi_set_ssid_password(${ssid}, ${password});\n`;
  };

  jsGenerator.forBlock['iot_wifi_connect'] = function () {
    return 'wifi_connect();\n';
  };

  jsGenerator.forBlock['iot_wifi_is_connected'] = function () {
    return ['wifi_is_connected()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['iot_mqtt_init'] = function (block, generator) {
    const host = generator.valueToCode(block, 'HOST', jsGenerator.ORDER_NONE) || '"broker.emqx.io"';
    const port = generator.valueToCode(block, 'PORT', jsGenerator.ORDER_NONE) || '1883';
    const clientId = generator.valueToCode(block, 'CLIENT_ID', jsGenerator.ORDER_NONE) || '"yiblock2"';
    const username = generator.valueToCode(block, 'USERNAME', jsGenerator.ORDER_NONE) || '""';
    const password = generator.valueToCode(block, 'PASSWORD', jsGenerator.ORDER_NONE) || '""';
    return `mqtt_init(${host}, ${port}, ${clientId}, ${username}, ${password});\n`;
  };

  jsGenerator.forBlock['iot_mqtt_connect'] = function () {
    return 'mqtt_connect();\n';
  };

  jsGenerator.forBlock['iot_mqtt_is_connected'] = function () {
    return ['mqtt_is_connected()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['iot_mqtt_subscribe'] = function (block, generator) {
    const topic = generator.valueToCode(block, 'TOPIC', jsGenerator.ORDER_NONE) || '"topic/demo"';
    return `mqtt_subscribe(${topic});\n`;
  };

  jsGenerator.forBlock['iot_mqtt_publish'] = function (block, generator) {
    const topic = generator.valueToCode(block, 'TOPIC', jsGenerator.ORDER_NONE) || '"topic/demo"';
    const payload = generator.valueToCode(block, 'PAYLOAD', jsGenerator.ORDER_NONE) || '""';
    return `mqtt_publish(${topic}, ${payload});\n`;
  };

  jsGenerator.forBlock['iot_mqtt_disconnect'] = function () {
    return 'mqtt_disconnect();\n';
  };

  jsonOutputGeneratorConfigs.forEach(({ type, args }) => {
    jsGenerator.forBlock[type] = function (block, generator) {
      const callArgs = getGeneratorCallArgs(block, generator, args, jsGenerator.ORDER_NONE);
      return [`${type}(${callArgs.join(', ')})`, jsGenerator.ORDER_FUNCTION_CALL];
    };
  });

  jsonStatementGeneratorConfigs.forEach(({ type, args }) => {
    jsGenerator.forBlock[type] = function (block, generator) {
      const callArgs = getGeneratorCallArgs(block, generator, args, jsGenerator.ORDER_NONE);
      const objectVariable = type.startsWith('json_object_set_') ? callArgs[0] : '';
      const objectInitialization = objectVariable
        ? `if (typeof ${objectVariable} === 'undefined' || ${objectVariable} === null || Array.isArray(${objectVariable})) { ${objectVariable} = json_create_object(); }\n`
        : '';
      return `${objectInitialization}${type}(${callArgs.join(', ')});\n`;
    };
  });

  jsGenerator.forBlock['json_create_object_var'] = function (block, generator) {
    const variableName = getJsonVariableSymbolName(block, 'OBJ_VAR', generator, 'json_object_1');
    return `var ${variableName} = json_create_object();\n`;
  };

  jsGenerator.forBlock['json_create_array_var'] = function (block, generator) {
    const variableName = getJsonVariableSymbolName(block, 'ARR_VAR', generator, 'json_array_1');
    return `var ${variableName} = json_create_array();\n`;
  };

  const declareReusableJsVariable = (variableName: string, initializer: string): string => {
    const normalized = String(variableName || '').trim();
    if (!normalized) return '';
    return `var ${normalized} = typeof ${normalized} !== 'undefined' ? ${normalized} : (${initializer});\n`;
  };

  jsGenerator.forBlock['wireless_peripheral_connect'] = function (block) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
    const rawDeviceId = block.getFieldValue('DEVICE_ID') || '';
    const deviceId = rawDeviceId.replace(/[^0-9a-zA-Z]/g, '').slice(0, 6);
    return [
      `await connectWirelessPeripheral('${peripheral}', '${peripheral}-', '${deviceId}')`,
      jsGenerator.ORDER_FUNCTION_CALL,
    ];
  };

  jsGenerator.forBlock['wireless_peripheral_create_object'] = function (block, generator) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
    const fallback = `${peripheral.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()}_obj`;
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, fallback);
    return declareReusableJsVariable(objectVar, 'null');
  };

  jsGenerator.forBlock['wireless_peripheral_set_object'] = function (block, generator) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
    const fallback = `${peripheral.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()}_obj`;
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, fallback).trim();
    if (!objectVar) return '';
    const handleCode = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return `${objectVar} = ${handleCode};\n`;
  };

  jsGenerator.forBlock['wireless_peripheral_disconnect'] = function (block, generator) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
    const fallback = `${peripheral.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()}_obj`;
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, fallback) || '0';
    return `await disconnectWirelessPeripheral(${handle}, '${peripheral}');\n`;
  };

  jsGenerator.forBlock['wired_peripheral_action'] = function (block) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YN-LED';
    return `runWiredPeripheralAction('${peripheral}');\n`;
  };

  jsGenerator.forBlock['yc_ui_screen_create'] = function (block, generator) {
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_screen_1');
    return declareReusableJsVariable(objectVar, 'lvObjCreate(null)');
  };

  jsGenerator.forBlock['yc_ui_set_active_screen'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return `lvScreenLoad(${handle});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '"标签文本"';
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    return `${declareReusableJsVariable(objectVar, `lvLabelCreate(${parent})`)}lvLabelSetText(${objectVar}, ${text});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_text'] = function (block, generator) {
    const handle = block.getField('OBJ_VAR')
      ? getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1')
      : (generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null');
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return `lvLabelSetText(${handle}, ${text});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_align'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const align = block.getFieldValue('ALIGN') || 'LV_ALIGN_CENTER';
    return `lvObjAlign(${handle}, ${JSON.stringify(align)}, 0, 0);\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_font'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const font = block.getFieldValue('FONT') || 'font_puhui_14_1';
    return `lvObjSetStyleTextFont(${handle}, ${JSON.stringify(font)});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_text_color'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const color = toRgb24Literal(block.getFieldValue('COLOR') || '#000000');
    return `lvObjSetStyleTextColor(${handle}, ${JSON.stringify(color)});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_bg_color'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const color = toRgb24Literal(block.getFieldValue('COLOR') || '#FFFFFF');
    return `lvObjSetStyleBgColor(${handle}, ${JSON.stringify(color)});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_pos'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const x = generator.valueToCode(block, 'X', jsGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', jsGenerator.ORDER_NONE) || '0';
    return `lvObjSetPos(${handle}, ${x}, ${y});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_size'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const width = generator.valueToCode(block, 'WIDTH', jsGenerator.ORDER_NONE) || '120';
    const height = generator.valueToCode(block, 'HEIGHT', jsGenerator.ORDER_NONE) || '32';
    return `lvObjSetSize(${handle}, ${width}, ${height});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_width'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const width = generator.valueToCode(block, 'WIDTH', jsGenerator.ORDER_NONE) || '120';
    return `lvObjSetWidth(${handle}, ${width});\n`;
  };

  jsGenerator.forBlock['yc_ui_label_set_height'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const height = generator.valueToCode(block, 'HEIGHT', jsGenerator.ORDER_NONE) || '32';
    return `lvObjSetHeight(${handle}, ${height});\n`;
  };

  jsGenerator.forBlock['yc_ui_button_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const api = block.getFieldValue('API') || 'lv_btn_create';
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_button_1');
    return declareReusableJsVariable(objectVar, `${api}(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_button_set_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return `lvButtonSetText(${handle}, ${text});\n`;
  };

  jsGenerator.forBlock['yc_ui_input_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_input_1');
    return declareReusableJsVariable(objectVar, `lvInputCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_input_set_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return `lvInputSetText(${handle}, ${text});\n`;
  };

  jsGenerator.forBlock['yc_ui_slider_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_slider_1');
    return declareReusableJsVariable(objectVar, `lvSliderCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_slider_set_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '0';
    return `lvSliderSetValue(${handle}, ${value});\n`;
  };

  jsGenerator.forBlock['yc_ui_progress_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_progress_1');
    return declareReusableJsVariable(objectVar, `lvProgressCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_progress_set_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '0';
    return `lvProgressSetValue(${handle}, ${value});\n`;
  };

  jsGenerator.forBlock['yc_ui_image_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_image_1');
    return declareReusableJsVariable(objectVar, `lvImageCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_image_set_src'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const src = generator.valueToCode(block, 'SRC', jsGenerator.ORDER_NONE) || '""';
    return `lvImageSetSrc(${handle}, ${src});\n`;
  };

  jsGenerator.forBlock['yc_ui_obj_center'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    return `lvObjCenter(${handle});\n`;
  };

  jsGenerator.forBlock['yc_ui_obj_set_scrollbar_mode'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const mode = block.getFieldValue('MODE') || 'LV_SCROLLBAR_MODE_AUTO';
    return `lvObjSetScrollbarMode(${handle}, ${JSON.stringify(mode)});\n`;
  };

  jsGenerator.forBlock['yc_ui_obj_add_flag'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const flag = block.getFieldValue('FLAG') || 'LV_OBJ_FLAG_CLICKABLE';
    return `lvObjAddFlag(${handle}, ${JSON.stringify(flag)});\n`;
  };

  jsGenerator.forBlock['yc_ui_obj_clear_flag'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const flag = block.getFieldValue('FLAG') || 'LV_OBJ_FLAG_CLICKABLE';
    return `lvObjClearFlag(${handle}, ${JSON.stringify(flag)});\n`;
  };

  jsGenerator.forBlock['yc_ui_obj_has_flag'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const flag = block.getFieldValue('FLAG') || 'LV_OBJ_FLAG_CLICKABLE';
    return [`lvObjHasFlag(${handle}, ${JSON.stringify(flag)})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_label_set_long_mode'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const mode = block.getFieldValue('MODE') || 'LV_LABEL_LONG_WRAP';
    return `lvLabelSetLongMode(${handle}, ${JSON.stringify(mode)});\n`;
  };

  jsGenerator.forBlock['yc_ui_checkbox_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_checkbox_1');
    return declareReusableJsVariable(objectVar, `lvCheckboxCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_checkbox_set_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return `lvCheckboxSetText(${handle}, ${text});\n`;
  };

  jsGenerator.forBlock['yc_ui_checkbox_get_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return [`lvCheckboxGetText(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_switch_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_switch_1');
    return declareReusableJsVariable(objectVar, `lvSwitchCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_input_add_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return `lvInputAddText(${handle}, ${text});\n`;
  };

  jsGenerator.forBlock['yc_ui_input_get_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return [`lvInputGetText(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_input_set_placeholder'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return `lvInputSetPlaceholderText(${handle}, ${text});\n`;
  };

  jsGenerator.forBlock['yc_ui_slider_set_mode'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const mode = block.getFieldValue('MODE') || 'LV_SLIDER_MODE_NORMAL';
    return `lvSliderSetMode(${handle}, ${JSON.stringify(mode)});\n`;
  };

  jsGenerator.forBlock['yc_ui_slider_get_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return [`lvSliderGetValue(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_progress_set_range'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const min = generator.valueToCode(block, 'MIN', jsGenerator.ORDER_NONE) || '0';
    const max = generator.valueToCode(block, 'MAX', jsGenerator.ORDER_NONE) || '100';
    return `lvProgressSetRange(${handle}, ${min}, ${max});\n`;
  };

  jsGenerator.forBlock['yc_ui_progress_set_mode'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const mode = block.getFieldValue('MODE') || 'LV_BAR_MODE_NORMAL';
    return `lvProgressSetMode(${handle}, ${JSON.stringify(mode)});\n`;
  };

  jsGenerator.forBlock['yc_ui_progress_get_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return [`lvProgressGetValue(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_arc_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_arc_1');
    return declareReusableJsVariable(objectVar, `lvArcCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_arc_set_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const value = generator.valueToCode(block, 'VALUE', jsGenerator.ORDER_NONE) || '0';
    return `lvArcSetValue(${handle}, ${value});\n`;
  };

  jsGenerator.forBlock['yc_ui_arc_set_range'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const min = generator.valueToCode(block, 'MIN', jsGenerator.ORDER_NONE) || '0';
    const max = generator.valueToCode(block, 'MAX', jsGenerator.ORDER_NONE) || '100';
    return `lvArcSetRange(${handle}, ${min}, ${max});\n`;
  };

  jsGenerator.forBlock['yc_ui_arc_set_angles'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const start = generator.valueToCode(block, 'START', jsGenerator.ORDER_NONE) || '0';
    const end = generator.valueToCode(block, 'END', jsGenerator.ORDER_NONE) || '360';
    return `lvArcSetAngles(${handle}, ${start}, ${end});\n`;
  };

  jsGenerator.forBlock['yc_ui_arc_set_bg_angles'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const start = generator.valueToCode(block, 'START', jsGenerator.ORDER_NONE) || '0';
    const end = generator.valueToCode(block, 'END', jsGenerator.ORDER_NONE) || '360';
    return `lvArcSetBgAngles(${handle}, ${start}, ${end});\n`;
  };

  jsGenerator.forBlock['yc_ui_arc_set_rotation'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const rotation = generator.valueToCode(block, 'ROTATION', jsGenerator.ORDER_NONE) || '0';
    return `lvArcSetRotation(${handle}, ${rotation});\n`;
  };

  jsGenerator.forBlock['yc_ui_arc_set_mode'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const mode = block.getFieldValue('MODE') || 'LV_ARC_MODE_NORMAL';
    return `lvArcSetMode(${handle}, ${JSON.stringify(mode)});\n`;
  };

  jsGenerator.forBlock['yc_ui_arc_get_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return [`lvArcGetValue(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_dropdown_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_dropdown_1');
    return declareReusableJsVariable(objectVar, `lvDropdownCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_dropdown_set_options'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const options = generator.valueToCode(block, 'OPTIONS', jsGenerator.ORDER_NONE) || '""';
    return `lvDropdownSetOptions(${handle}, ${options});\n`;
  };

  jsGenerator.forBlock['yc_ui_dropdown_add_option'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const option = generator.valueToCode(block, 'OPTION', jsGenerator.ORDER_NONE) || '""';
    const position = generator.valueToCode(block, 'POSITION', jsGenerator.ORDER_NONE) || '0';
    return `lvDropdownAddOption(${handle}, ${option}, ${position});\n`;
  };

  jsGenerator.forBlock['yc_ui_dropdown_clear_options'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return `lvDropdownClearOptions(${handle});\n`;
  };

  jsGenerator.forBlock['yc_ui_dropdown_set_selected'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const selected = generator.valueToCode(block, 'SELECTED', jsGenerator.ORDER_NONE) || '0';
    return `lvDropdownSetSelected(${handle}, ${selected});\n`;
  };

  jsGenerator.forBlock['yc_ui_dropdown_get_selected'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return [`lvDropdownGetSelected(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_dropdown_get_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return [`lvDropdownGetText(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_list_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'null');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_list_1');
    return declareReusableJsVariable(objectVar, `lvListCreate(${parent})`);
  };

  jsGenerator.forBlock['yc_ui_list_add_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return [`lvListAddText(${handle}, ${text})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_list_add_btn'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const symbol = block.getFieldValue('SYMBOL') || 'NULL';
    const symbolCode = symbol === 'NULL' ? 'null' : JSON.stringify(symbol);
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return [`lvListAddBtn(${handle}, ${symbolCode}, ${text})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['yc_ui_list_get_btn_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const btn = generator.valueToCode(block, 'BTN', jsGenerator.ORDER_NONE) || 'null';
    return [`lvListGetBtnText(${handle}, ${btn})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['cs_ui_screen_create'] = function (block, generator) {
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_screen_1');
    return declareReusableJsVariable(objectVar, 'uiScrCreate()');
  };

  jsGenerator.forBlock['cs_ui_screen_show'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return `uiScrShow(${handle});\n`;
  };

  jsGenerator.forBlock['cs_ui_lable_create'] = function (block, generator) {
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const parent = generator.valueToCode(block, 'PARENT', jsGenerator.ORDER_NONE) || 'null';
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    const x = generator.valueToCode(block, 'X', jsGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', jsGenerator.ORDER_NONE) || '0';
    const w = generator.valueToCode(block, 'W', jsGenerator.ORDER_NONE) || '100';
    const h = generator.valueToCode(block, 'H', jsGenerator.ORDER_NONE) || '40';
    return declareReusableJsVariable(objectVar, `uiLabelCreate(${parent}, ${text}, ${x}, ${y}, ${w}, ${h})`);
  };

  jsGenerator.forBlock['cs_ui_label_set_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const text = generator.valueToCode(block, 'TEXT', jsGenerator.ORDER_NONE) || '""';
    return `uiSetText(${handle}, ${text});\n`;
  };

  jsGenerator.forBlock['cs_ui_label_set_font'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const font = getUiSimpleFontValueFromBlock(block, generator, '1');
    return `uiSetFont(${handle}, ${font});\n`;
  };

  jsGenerator.forBlock['cs_ui_label_set_color'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const color = getUiSimpleColorLiteralFromBlock(block, generator, 'COLOR', '0xFFFFFF');
    return `uiSetColor(${handle}, ${color});\n`;
  };

  jsGenerator.forBlock['cs_ui_label_set_align'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const align = getUiSimpleAlignValueFromBlock(block, generator, '0');
    return `uiSetAlign(${handle}, ${align});\n`;
  };

  jsGenerator.forBlock['cs_ui_set_xy'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const x = generator.valueToCode(block, 'X', jsGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', jsGenerator.ORDER_NONE) || '0';
    return `uiSetXY(${handle}, ${x}, ${y});\n`;
  };

  jsGenerator.forBlock['cs_ui_delete'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    return `uiDelete(${handle});\n`;
  };

  jsGenerator.forBlock['cs_ui_set_background_color'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || 'null';
    const color = getUiSimpleColorLiteralFromBlock(block, generator, 'COLOR', '0xFFFFFF');
    return `uiSetBgColor(${handle}, ${color});\n`;
  };

  jsGenerator.forBlock['ym_led_set_power'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const state = block.getFieldValue('STATE') || '0';
    return `await ymLedSetPower(${handle}, ${Number(state)});\n`;
  };

  jsGenerator.forBlock['ym_led_set_brightness'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const brightness =
      generator.valueToCode(block, 'BRIGHTNESS', jsGenerator.ORDER_NONE)
      || block.getFieldValue('BRIGHTNESS')
      || '0';
    return `await ymLedSetBrightness(${handle}, ${brightness});\n`;
  };

  jsGenerator.forBlock['ym_led_flash_once'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const duration = generator.valueToCode(block, 'DURATION', jsGenerator.ORDER_NONE) || '100';
    return `await ymLedFlashOnce(${handle}, ${duration});\n`;
  };

  jsGenerator.forBlock['ym_au_play_audio'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const audio = block.getFieldValue('AUDIO') || '1';
    return `await ymAuPlayAudio(${handle}, ${Number(audio)});\n`;
  };

  jsGenerator.forBlock['ym_au_record_control'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const control = block.getFieldValue('CONTROL') || '0';
    return `await ymAuRecordControl(${handle}, ${Number(control)});\n`;
  };

  jsGenerator.forBlock['ym_pir_read_status'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymPirReadStatus(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_pir_when_approach'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const statements = generator.statementToCode(block, 'DO');
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `onGreenFlag(async () => {\n  let __pirPrev_${key} = await ymPirReadStatus(${handle});\n  while (true) {\n    const __pirCurr_${key} = await ymPirReadStatus(${handle});\n    if (__pirPrev_${key} === 0 && __pirCurr_${key} === 1) {\n${statements}    }\n    __pirPrev_${key} = __pirCurr_${key};\n    await waitSeconds(0.2);\n  }\n});\n`;
  };

  jsGenerator.forBlock['ym_pir_when_leave'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const statements = generator.statementToCode(block, 'DO');
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `onGreenFlag(async () => {\n  let __pirPrev_${key} = await ymPirReadStatus(${handle});\n  while (true) {\n    const __pirCurr_${key} = await ymPirReadStatus(${handle});\n    if (__pirPrev_${key} === 1 && __pirCurr_${key} === 0) {\n${statements}    }\n    __pirPrev_${key} = __pirCurr_${key};\n    await waitSeconds(0.2);\n  }\n});\n`;
  };

  jsGenerator.forBlock['ym_dlgt_read_light'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymDlgtReadLight(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_beep_set_frequency'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const frequency = generator.valueToCode(block, 'FREQUENCY', jsGenerator.ORDER_NONE) || '440';
    return `await ymBeepSetFrequency(${handle}, ${frequency});\n`;
  };

  jsGenerator.forBlock['ym_beep_set_power'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const state = block.getFieldValue('STATE') || '0';
    return `await ymBeepSetPower(${handle}, ${Number(state)});\n`;
  };

  jsGenerator.forBlock['ym_ryg_set_light'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const color = block.getFieldValue('COLOR') || 'red';
    const state = block.getFieldValue('STATE') || '0';
    return `await ymRygSetLight(${handle}, "${color}", ${Number(state)});\n`;
  };

  jsGenerator.forBlock['ym_k3_read_status'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymK3ReadStatus(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_k3_when_black_pressed'] = function (block, generator) {
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `runtimeLog("YM-K3 黑键事件：已登记到主运行流程");\nonK3ButtonPressed(0x01, async () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['ym_k3_when_red_pressed'] = function (block, generator) {
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `runtimeLog("YM-K3 红键事件：已登记到主运行流程");\nonK3ButtonPressed(0x02, async () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['ym_k3_when_green_pressed'] = function (block, generator) {
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `runtimeLog("YM-K3 绿键事件：已登记到主运行流程");\nonK3ButtonPressed(0x04, async () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['ym_swusb_set_power'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const state = block.getFieldValue('STATE') || '0';
    return `await ymSwusbSetPower(${handle}, ${Number(state)});\n`;
  };

  jsGenerator.forBlock['ym_swusb_set_level'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const level = generator.valueToCode(block, 'LEVEL', jsGenerator.ORDER_NONE) || '100';
    return `await ymSwusbSetLevel(${handle}, ${level});\n`;
  };

  jsGenerator.forBlock['ym_th1_read_temperature'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymTh1ReadTemperature(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_th1_read_humidity'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymTh1ReadHumidity(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_satellites'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadSatellites(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_beidou_satellites'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadBeidouSatellites(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_gps_satellites'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadGpsSatellites(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_speed'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadSpeed(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_angle'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadAngle(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_acceleration'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadAcceleration(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_time'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadTime(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_position_status'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadPositionStatus(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_altitude'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymGnssReadAltitude(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_latitude'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`Number.parseFloat(await ymGnssReadLatitude(${handle})) || 0`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_read_longitude'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`Number.parseFloat(await ymGnssReadLongitude(${handle})) || 0`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_gnss_calculate_distance'] = function (block, generator) {
    const latitude1 = generator.valueToCode(block, 'LAT1', jsGenerator.ORDER_NONE) || '0';
    const longitude1 = generator.valueToCode(block, 'LON1', jsGenerator.ORDER_NONE) || '0';
    const latitude2 = generator.valueToCode(block, 'LAT2', jsGenerator.ORDER_NONE) || '0';
    const longitude2 = generator.valueToCode(block, 'LON2', jsGenerator.ORDER_NONE) || '0';
    return [`((lat1, lon1, lat2, lon2) => {
  const radians = Math.PI / 180;
  const deltaLat = (lat2 - lat1) * radians;
  const deltaLon = (lon2 - lon1) * radians;
  const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1 * radians) * Math.cos(lat2 * radians) * Math.sin(deltaLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
})(${latitude1}, ${longitude1}, ${latitude2}, ${longitude2})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_us_read_distance'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ymUsReadDistance(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['ym_5g_read_signal_strength'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadSignalStrength(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_read_imei'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadImei(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_read_operator'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadOperator(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_read_iccid'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadIccid(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_read_sim_id'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadSimId(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_set_sim_id'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const simId = block.getFieldValue('SIM_ID') || '0';
    return `await ym5gSetSimId(${handle}, ${Number(simId)});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_data'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadData(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_read_state'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadState(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_read_mqtt_connect'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadMqttConnect(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_mqtt_connect'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return `await ym5gSetMqttConnect(${handle}, 1);\n`;
  };
  jsGenerator.forBlock['ym_5g_mqtt_disconnect'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return `await ym5gSetMqttConnect(${handle}, 0);\n`;
  };
  jsGenerator.forBlock['ym_5g_read_user'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadUser(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_set_user'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const user = generator.valueToCode(block, 'USER', jsGenerator.ORDER_NONE) || '""';
    return `await ym5gSetUser(${handle}, ${user});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_password'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadPassword(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_set_password'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const password = generator.valueToCode(block, 'PASSWORD', jsGenerator.ORDER_NONE) || '""';
    return `await ym5gSetPassword(${handle}, ${password});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_qos'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadQos(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_set_qos'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const qos = generator.valueToCode(block, 'QOS', jsGenerator.ORDER_NONE) || '2';
    return `await ym5gSetQos(${handle}, ${qos});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_topic'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadTopic(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_mqtt_subscribe'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const topic = generator.valueToCode(block, 'TOPIC', jsGenerator.ORDER_NONE) || '""';
    return `await ym5gSetTopic(${handle}, ${topic});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_client_id'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadClientId(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_set_client_id'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const clientId = generator.valueToCode(block, 'CLIENT_ID', jsGenerator.ORDER_NONE) || '""';
    return `await ym5gSetClientId(${handle}, ${clientId});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_host'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadHost(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_set_host'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const host = generator.valueToCode(block, 'HOST', jsGenerator.ORDER_NONE) || '""';
    return `await ym5gSetHost(${handle}, ${host});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_port'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadPort(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_set_port'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const port = generator.valueToCode(block, 'PORT', jsGenerator.ORDER_NONE) || '1883';
    return `await ym5gSetPort(${handle}, ${port});\n`;
  };
  jsGenerator.forBlock['ym_5g_set_mqtt_config'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const clientId = generator.valueToCode(block, 'CLIENT_ID', jsGenerator.ORDER_NONE) || '"YM-5G-XXXXX"';
    const host = generator.valueToCode(block, 'HOST', jsGenerator.ORDER_NONE) || '"broker.emqx.io"';
    const port = generator.valueToCode(block, 'PORT', jsGenerator.ORDER_NONE) || '1883';
    const user = generator.valueToCode(block, 'USER', jsGenerator.ORDER_NONE) || '"user"';
    const password = generator.valueToCode(block, 'PASSWORD', jsGenerator.ORDER_NONE) || '"password"';
    return `await ym5gSetMqttConfig(${handle}, ${clientId}, ${host}, ${port}, ${user}, ${password});\n`;
  };
  jsGenerator.forBlock['ym_5g_mqtt_publish'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const topic = generator.valueToCode(block, 'TOPIC', jsGenerator.ORDER_NONE) || '""';
    const message = generator.valueToCode(block, 'MESSAGE', jsGenerator.ORDER_NONE) || '""';
    return `await ym5gSetTopic(${handle}, ${topic});\nawait ym5gPublishMessage(${handle}, ${message});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_subscribe'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadSubscribe(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };
  jsGenerator.forBlock['ym_5g_on_subscribe_message'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    const payloadVar = getVariableSymbolName(block, 'PAYLOAD_VAR', generator, 'mqtt_msg');
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `onGreenFlag(async () => {\n  let __ym5gPrevMsg_${key} = await ym5gReadSubscribe(${handle});\n  while (true) {\n    const __ym5gCurrMsg_${key} = await ym5gReadSubscribe(${handle});\n    if (__ym5gCurrMsg_${key} && __ym5gCurrMsg_${key} !== __ym5gPrevMsg_${key}) {\n      const ${payloadVar} = __ym5gCurrMsg_${key};\n${statements}    }\n    __ym5gPrevMsg_${key} = __ym5gCurrMsg_${key};\n    await waitSeconds(0.2);\n  }\n});\n`;
  };
  jsGenerator.forBlock['ym_5g_read_mqtt_state'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', jsGenerator.ORDER_NONE) || '0';
    return [`await ym5gReadMqttState(${handle})`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['three_create_object'] = function (block) {
    const shape = block.getFieldValue('SHAPE') || 'cube';
    return [`create3DObject('${shape}')`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['three_transform'] = function (block, generator) {
    const target = generator.valueToCode(block, 'TARGET', jsGenerator.ORDER_NONE) || 'obj';
    const mode = block.getFieldValue('MODE') || 'move';
    const x = generator.valueToCode(block, 'X', jsGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', jsGenerator.ORDER_NONE) || '0';
    const z = generator.valueToCode(block, 'Z', jsGenerator.ORDER_NONE) || '0';
    return `transform3DObject(${target}, '${mode}', ${x}, ${y}, ${z});\n`;
  };

  jsGenerator.forBlock['three_on_key'] = function (block, generator) {
    const key = block.getFieldValue('KEY') || 'ArrowUp';
    const statements = generator.statementToCode(block, 'DO');
    return `onKeyPress('${key}', () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['three_on_collision'] = function (block, generator) {
    const target = generator.valueToCode(block, 'TARGET', jsGenerator.ORDER_NONE) || 'obj';
    const statements = generator.statementToCode(block, 'DO');
    return `onCollision(${target}, () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['scratch_stage_switch_backdrop'] = function (block, generator) {
    const backdrop = generator.valueToCode(block, 'BACKDROP', jsGenerator.ORDER_NONE) || '"背景"';
    return `stageSwitchBackdrop(${backdrop});\n`;
  };

  jsGenerator.forBlock['scratch_stage_next_backdrop'] = function () {
    return 'stageNextBackdrop();\n';
  };

  jsGenerator.forBlock['scratch_stage_set_mode'] = function (block) {
    const mode = block.getFieldValue('MODE') || 'flat';
    return `stageSetMode("${mode}");\n`;
  };

  jsGenerator.forBlock['scratch_stage_show'] = function () {
    return 'stageShow();\n';
  };

  jsGenerator.forBlock['scratch_stage_hide'] = function () {
    return 'stageHide();\n';
  };

  jsGenerator.forBlock['scratch_motion_move_steps'] = function (block, generator) {
    const steps = generator.valueToCode(block, 'STEPS', jsGenerator.ORDER_NONE) || '10';
    return `moveSteps(${steps});\n`;
  };

  jsGenerator.forBlock['scratch_motion_turn_right'] = function (block, generator) {
    const degrees = generator.valueToCode(block, 'DEGREES', jsGenerator.ORDER_NONE) || '15';
    return `turnRight(${degrees});\n`;
  };

  jsGenerator.forBlock['scratch_motion_turn_left'] = function (block, generator) {
    const degrees = generator.valueToCode(block, 'DEGREES', jsGenerator.ORDER_NONE) || '15';
    return `turnLeft(${degrees});\n`;
  };

  jsGenerator.forBlock['scratch_motion_go_to_xy'] = function (block, generator) {
    const x = generator.valueToCode(block, 'X', jsGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', jsGenerator.ORDER_NONE) || '0';
    return `goToXY(${x}, ${y});\n`;
  };

  jsGenerator.forBlock['scratch_motion_glide_to_xy'] = function (block, generator) {
    const secs = generator.valueToCode(block, 'SECS', jsGenerator.ORDER_NONE) || '1';
    const x = generator.valueToCode(block, 'X', jsGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', jsGenerator.ORDER_NONE) || '0';
    return `glideToXY(${secs}, ${x}, ${y});\n`;
  };

  jsGenerator.forBlock['scratch_motion_point_in_direction'] = function (block, generator) {
    const direction = generator.valueToCode(block, 'DIRECTION', jsGenerator.ORDER_NONE) || '90';
    return `pointInDirection(${direction});\n`;
  };

  jsGenerator.forBlock['scratch_motion_set_x'] = function (block, generator) {
    const x = generator.valueToCode(block, 'X', jsGenerator.ORDER_NONE) || '0';
    return `setX(${x});\n`;
  };

  jsGenerator.forBlock['scratch_motion_set_y'] = function (block, generator) {
    const y = generator.valueToCode(block, 'Y', jsGenerator.ORDER_NONE) || '0';
    return `setY(${y});\n`;
  };

  jsGenerator.forBlock['scratch_motion_change_x'] = function (block, generator) {
    const dx = generator.valueToCode(block, 'DX', jsGenerator.ORDER_NONE) || '10';
    return `changeX(${dx});\n`;
  };

  jsGenerator.forBlock['scratch_motion_change_y'] = function (block, generator) {
    const dy = generator.valueToCode(block, 'DY', jsGenerator.ORDER_NONE) || '10';
    return `changeY(${dy});\n`;
  };

  jsGenerator.forBlock['scratch_motion_if_on_edge_bounce'] = function () {
    return 'ifOnEdgeBounce();\n';
  };

  jsGenerator.forBlock['scratch_motion_x_position'] = function () {
    return ['getXPosition()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_motion_y_position'] = function () {
    return ['getYPosition()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_motion_direction'] = function () {
    return ['getDirection()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_stage_set_speech_voice'] = function (block) {
    return `setStageSpeechVoice(${JSON.stringify(block.getFieldValue('VOICE') || '')});\n`;
  };

  jsGenerator.forBlock['scratch_looks_say'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', jsGenerator.ORDER_NONE) || '"Hello"';
    return `say(${message});\n`;
  };

  jsGenerator.forBlock['scratch_looks_say_for_secs'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', jsGenerator.ORDER_NONE) || '"Hello"';
    const secs = generator.valueToCode(block, 'SECS', jsGenerator.ORDER_NONE) || '2';
    return `sayForSeconds(${message}, ${secs});\n`;
  };

  jsGenerator.forBlock['scratch_looks_think'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', jsGenerator.ORDER_NONE) || '"Hmm"';
    return `think(${message});\n`;
  };

  jsGenerator.forBlock['scratch_looks_think_for_secs'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', jsGenerator.ORDER_NONE) || '"Hmm"';
    const secs = generator.valueToCode(block, 'SECS', jsGenerator.ORDER_NONE) || '2';
    return `thinkForSeconds(${message}, ${secs});\n`;
  };

  jsGenerator.forBlock['scratch_looks_show'] = function () {
    return 'showSprite();\n';
  };

  jsGenerator.forBlock['scratch_looks_hide'] = function () {
    return 'hideSprite();\n';
  };

  jsGenerator.forBlock['scratch_looks_switch_costume'] = function (block, generator) {
    const costume = generator.valueToCode(block, 'COSTUME', jsGenerator.ORDER_NONE) || '"造型1"';
    return `switchCostume(${costume});\n`;
  };

  jsGenerator.forBlock['scratch_looks_next_costume'] = function () {
    return 'nextCostume();\n';
  };

  jsGenerator.forBlock['scratch_looks_change_size'] = function (block, generator) {
    const size = generator.valueToCode(block, 'SIZE', jsGenerator.ORDER_NONE) || '10';
    return `changeSize(${size});\n`;
  };

  jsGenerator.forBlock['scratch_looks_set_size'] = function (block, generator) {
    const size = generator.valueToCode(block, 'SIZE', jsGenerator.ORDER_NONE) || '100';
    return `setSize(${size});\n`;
  };

  jsGenerator.forBlock['scratch_looks_size'] = function () {
    return ['getSize()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sound_play'] = function (block, generator) {
    const sound = generator.valueToCode(block, 'SOUND', jsGenerator.ORDER_NONE) || '"sound1"';
    return `playSound(${sound});\n`;
  };

  jsGenerator.forBlock['scratch_sound_play_until_done'] = function (block, generator) {
    const sound = generator.valueToCode(block, 'SOUND', jsGenerator.ORDER_NONE) || '"sound1"';
    return `playSoundUntilDone(${sound});\n`;
  };

  jsGenerator.forBlock['scratch_sound_stop_all'] = function () {
    return 'stopAllSounds();\n';
  };

  jsGenerator.forBlock['scratch_sound_change_volume'] = function (block, generator) {
    const volume = generator.valueToCode(block, 'VOLUME', jsGenerator.ORDER_NONE) || '10';
    return `changeVolume(${volume});\n`;
  };

  jsGenerator.forBlock['scratch_sound_set_volume'] = function (block, generator) {
    const volume = generator.valueToCode(block, 'VOLUME', jsGenerator.ORDER_NONE) || '100';
    return `setVolume(${volume});\n`;
  };

  jsGenerator.forBlock['scratch_sound_volume'] = function () {
    return ['getVolume()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  const javaScriptGeneratorWithScrub = jsGenerator as unknown as GeneratorWithScrub;
  const originalJavaScriptScrub = javaScriptGeneratorWithScrub.scrub_;
  javaScriptGeneratorWithScrub.scrub_ = function (block, code, thisOnly) {
    if (
      block.type === 'scratch_event_when_green_flag' ||
      block.type === 'scratch_event_when_key_pressed' ||
      block.type === 'scratch_event_when_sprite_clicked' ||
      block.type === 'ym_k3_when_black_pressed' ||
      block.type === 'ym_k3_when_red_pressed' ||
      block.type === 'ym_k3_when_green_pressed' ||
      block.type === 'ym_5g_on_subscribe_message'
    ) {
      return code;
    }
    return originalJavaScriptScrub.call(this, block, code, thisOnly);
  };

  jsGenerator.forBlock['scratch_event_when_green_flag'] = function (block, generator) {
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `onGreenFlag(async () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['scratch_event_when_key_pressed'] = function (block, generator) {
    const key = block.getFieldValue('KEY') || 'Space';
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `onKeyPressed("${key}", async () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['scratch_event_when_sprite_clicked'] = function (block, generator) {
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `onSpriteClicked(async () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['scratch_event_broadcast'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', jsGenerator.ORDER_NONE) || '"message1"';
    return `broadcast(${message});\n`;
  };

  jsGenerator.forBlock['scratch_event_broadcast_and_wait'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', jsGenerator.ORDER_NONE) || '"message1"';
    return `await broadcastAndWait(${message});\n`;
  };

  jsGenerator.forBlock['scratch_event_when_receive'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', jsGenerator.ORDER_NONE) || '"message1"';
    const statements = generator.statementToCode(block, 'DO');
    return `onBroadcast(${message}, async () => {\n${statements}});\n`;
  };

  jsGenerator.forBlock['scratch_control_wait'] = function (block, generator) {
    const secs = generator.valueToCode(block, 'SECS', jsGenerator.ORDER_NONE) || '1';
    return `await waitSeconds(${secs});\n`;
  };

  jsGenerator.forBlock['scratch_control_system_beep'] = function (block, generator) {
    const duration = generator.valueToCode(block, 'DURATION', jsGenerator.ORDER_NONE) || '200';
    const freq = generator.valueToCode(block, 'FREQ', jsGenerator.ORDER_NONE) || '880';
    return `await systemBeep(${duration}, ${freq});\n`;
  };

  jsGenerator.forBlock['scratch_control_wait_until'] = function (block, generator) {
    const condition = generator.valueToCode(block, 'CONDITION', jsGenerator.ORDER_NONE) || 'false';
    return `await waitUntil(() => ${condition});\n`;
  };

  jsGenerator.forBlock['scratch_control_repeat'] = function (block, generator) {
    const times = generator.valueToCode(block, 'TIMES', jsGenerator.ORDER_NONE) || '10';
    const statements = generator.statementToCode(block, 'DO');
    return `for (let i = 0; i < ${times}; i++) {\n${statements}}\n`;
  };

  jsGenerator.forBlock['scratch_control_forever'] = function (block, generator) {
    const statements = generator.statementToCode(block, 'DO');
    return `while (true) {\n${statements}await waitSeconds(0);\n}\n`;
  };

  jsGenerator.forBlock['scratch_control_repeat_until'] = function (block, generator) {
    const condition = generator.valueToCode(block, 'CONDITION', jsGenerator.ORDER_NONE) || 'false';
    const statements = generator.statementToCode(block, 'DO');
    return `while (!(${condition})) {\n${statements}await waitSeconds(0);\n}\n`;
  };

  jsGenerator.forBlock['scratch_control_stop'] = function (block) {
    const scope = block.getFieldValue('SCOPE') || 'all';
    return `stopScripts("${scope}");\n`;
  };

  jsGenerator.forBlock['scratch_control_create_clone_of'] = function (block, generator) {
    const target = generator.valueToCode(block, 'TARGET', jsGenerator.ORDER_NONE) || '"自己"';
    return `createCloneOf(${target});\n`;
  };

  jsGenerator.forBlock['scratch_control_delete_clone'] = function () {
    return 'deleteThisClone();\n';
  };

  jsGenerator.forBlock['scratch_sensing_touching'] = function (block) {
    const target = block.getFieldValue('TARGET') || 'mouse';
    return [`isTouching("${target}")`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_touching_color'] = function (block) {
    const color = block.getFieldValue('COLOR') || '#ff0000';
    return [`isTouchingColor("${color}")`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_color_touching_color'] = function (block) {
    const color1 = block.getFieldValue('COLOR1') || '#ff0000';
    const color2 = block.getFieldValue('COLOR2') || '#0000ff';
    return [`isColorTouchingColor("${color1}", "${color2}")`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_distance_to'] = function (block) {
    const target = block.getFieldValue('TARGET') || 'mouse';
    return [`distanceTo("${target}")`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_ask_and_wait'] = function (block, generator) {
    const question = generator.valueToCode(block, 'QUESTION', jsGenerator.ORDER_NONE) || '"?"';
    return `await askAndWait(${question});\n`;
  };

  jsGenerator.forBlock['scratch_sensing_answer'] = function () {
    return ['getAnswer()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_key_pressed'] = function (block) {
    const key = block.getFieldValue('KEY') || 'Space';
    return [`isKeyPressed("${key}")`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_mouse_down'] = function () {
    return ['isMouseDown()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_mouse_x'] = function () {
    return ['mouseX()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_mouse_y'] = function () {
    return ['mouseY()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_loudness'] = function () {
    return ['loudness()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_timer'] = function () {
    return ['timer()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_reset_timer'] = function () {
    return 'resetTimer();\n';
  };

  jsGenerator.forBlock['scratch_sensing_current_time'] = function (block) {
    const unit = block.getFieldValue('UNIT') || 'second';
    return [`currentTime("${unit}")`, jsGenerator.ORDER_FUNCTION_CALL];
  };

  jsGenerator.forBlock['scratch_sensing_days_since_2000'] = function () {
    return ['daysSince2000()', jsGenerator.ORDER_FUNCTION_CALL];
  };

  // ============ Python Generators ============
  pyGenerator.forBlock['text_number_to_string_custom'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '0';
    const digits = block.getFieldValue('DIGITS') || '6';
    return [`format(float(${value}), f".{max(0, int(${digits}))}f")`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['text_concat_two_custom'] = function (block, generator) {
    const left = generator.valueToCode(block, 'LEFT', pyGenerator.ORDER_NONE) || '""';
    const right = generator.valueToCode(block, 'RIGHT', pyGenerator.ORDER_NONE) || '""';
    return [`(str(${left}) + str(${right}))`, pyGenerator.ORDER_NONE];
  };

  pyGenerator.forBlock['json_parse_text'] = function (block, generator) {
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '"{}"';
    return [`(__import__("json").loads(str(${text})))`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['json_stringify_object'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '{}';
    return [`(__import__("json").dumps(${value}, ensure_ascii=False))`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yios_int2str'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '0';
    return [`str(int(${value}))`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yios_str2int'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '"0"';
    return [`int(float(str(${value})))`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yios_str2float'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '"0"';
    return [`float(str(${value}))`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yios_float2str'] = function (block, generator) {
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '0';
    return [`str(float(${value}))`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['iot_wifi_enable'] = function () {
    return 'wifi_enable()\n';
  };

  pyGenerator.forBlock['iot_wifi_set_ssid_password'] = function (block, generator) {
    const ssid = generator.valueToCode(block, 'SSID', pyGenerator.ORDER_NONE) || '"my-ssid"';
    const password = generator.valueToCode(block, 'PASSWORD', pyGenerator.ORDER_NONE) || '"my-password"';
    return `wifi_set_ssid_password(${ssid}, ${password})\n`;
  };

  pyGenerator.forBlock['iot_wifi_connect'] = function () {
    return 'wifi_connect()\n';
  };

  pyGenerator.forBlock['iot_wifi_is_connected'] = function () {
    return ['wifi_is_connected()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['iot_mqtt_init'] = function (block, generator) {
    const host = generator.valueToCode(block, 'HOST', pyGenerator.ORDER_NONE) || '"broker.emqx.io"';
    const port = generator.valueToCode(block, 'PORT', pyGenerator.ORDER_NONE) || '1883';
    const clientId = generator.valueToCode(block, 'CLIENT_ID', pyGenerator.ORDER_NONE) || '"yiblock2"';
    const username = generator.valueToCode(block, 'USERNAME', pyGenerator.ORDER_NONE) || '""';
    const password = generator.valueToCode(block, 'PASSWORD', pyGenerator.ORDER_NONE) || '""';
    return `mqtt_init(${host}, ${port}, ${clientId}, ${username}, ${password})\n`;
  };

  pyGenerator.forBlock['iot_mqtt_connect'] = function () {
    return 'mqtt_connect()\n';
  };

  pyGenerator.forBlock['iot_mqtt_is_connected'] = function () {
    return ['mqtt_is_connected()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['iot_mqtt_subscribe'] = function (block, generator) {
    const topic = generator.valueToCode(block, 'TOPIC', pyGenerator.ORDER_NONE) || '"topic/demo"';
    return `mqtt_subscribe(${topic})\n`;
  };

  pyGenerator.forBlock['iot_mqtt_publish'] = function (block, generator) {
    const topic = generator.valueToCode(block, 'TOPIC', pyGenerator.ORDER_NONE) || '"topic/demo"';
    const payload = generator.valueToCode(block, 'PAYLOAD', pyGenerator.ORDER_NONE) || '""';
    return `mqtt_publish(${topic}, ${payload})\n`;
  };

  pyGenerator.forBlock['iot_mqtt_disconnect'] = function () {
    return 'mqtt_disconnect()\n';
  };

  jsonOutputGeneratorConfigs.forEach(({ type, args }) => {
    pyGenerator.forBlock[type] = function (block, generator) {
      const callArgs = getGeneratorCallArgs(block, generator, args, pyGenerator.ORDER_NONE);
      return [`${type}(${callArgs.join(', ')})`, pyGenerator.ORDER_FUNCTION_CALL];
    };
  });

  jsonStatementGeneratorConfigs.forEach(({ type, args }) => {
    pyGenerator.forBlock[type] = function (block, generator) {
      const callArgs = getGeneratorCallArgs(block, generator, args, pyGenerator.ORDER_NONE);
      return `${type}(${callArgs.join(', ')})\n`;
    };
  });

  pyGenerator.forBlock['json_create_object_var'] = function (block, generator) {
    const variableName = getJsonVariableSymbolName(block, 'OBJ_VAR', generator, 'json_object_1');
    return `${variableName} = json_create_object()\n`;
  };

  pyGenerator.forBlock['json_create_array_var'] = function (block, generator) {
    const variableName = getJsonVariableSymbolName(block, 'ARR_VAR', generator, 'json_array_1');
    return `${variableName} = json_create_array()\n`;
  };

  pyGenerator.forBlock['wireless_peripheral_connect'] = function (block) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
    const rawDeviceId = block.getFieldValue('DEVICE_ID') || '';
    const deviceId = rawDeviceId.replace(/[^0-9a-zA-Z]/g, '').slice(0, 6);
    return [`connect_wireless_peripheral("${peripheral}", "${peripheral}-", "${deviceId}")`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['wireless_peripheral_create_object'] = function (block) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
    const fallback = `${peripheral.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()}_obj`;
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', pyGenerator, fallback);
    return `${objectVar} = None\n`;
  };

  pyGenerator.forBlock['wireless_peripheral_set_object'] = function (block, generator) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
    const fallback = `${peripheral.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()}_obj`;
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, fallback).trim();
    if (!objectVar) return '';
    const handleCode = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return `${objectVar} = ${handleCode}\n`;
  };

  pyGenerator.forBlock['wireless_peripheral_disconnect'] = function (block, generator) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YM-LED';
    const fallback = `${peripheral.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()}_obj`;
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, fallback) || 'None';
    return `disconnect_wireless_peripheral(${handle}, "${peripheral}")\n`;
  };

  pyGenerator.forBlock['wired_peripheral_action'] = function (block) {
    const peripheral = block.getFieldValue('PERIPHERAL') || 'YN-LED';
    return `run_wired_peripheral_action("${peripheral}")\n`;
  };

  pyGenerator.forBlock['yc_ui_screen_create'] = function (block, generator) {
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_screen_1');
    return `${objectVar} = lv_obj_create(None)\n`;
  };

  pyGenerator.forBlock['yc_ui_set_active_screen'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return `lv_scr_load(${handle})\n`;
  };

  pyGenerator.forBlock['yc_ui_label_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '"标签文本"';
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    return `${objectVar} = lv_label_create(${parent})\nlv_label_set_text(${objectVar}, ${text})\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_text'] = function (block, generator) {
    const handle = block.getField('OBJ_VAR')
      ? getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1')
      : (generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None');
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return `lv_label_set_text(${handle}, ${text})\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_align'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const align = block.getFieldValue('ALIGN') || 'LV_ALIGN_CENTER';
    return `lv_obj_align(${handle}, "${align}", 0, 0)\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_font'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const font = block.getFieldValue('FONT') || 'font_puhui_14_1';
    return `lv_obj_set_style_text_font(${handle}, "${font}", 0)\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_text_color'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const color = toRgb24Literal(block.getFieldValue('COLOR') || '#000000');
    return `lv_obj_set_style_text_color(${handle}, ${color}, 0)\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_bg_color'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const color = toRgb24Literal(block.getFieldValue('COLOR') || '#FFFFFF');
    return `lv_obj_set_style_bg_color(${handle}, ${color}, 0)\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_pos'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const x = generator.valueToCode(block, 'X', pyGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', pyGenerator.ORDER_NONE) || '0';
    return `lv_obj_set_pos(${handle}, ${x}, ${y})\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_size'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const width = generator.valueToCode(block, 'WIDTH', pyGenerator.ORDER_NONE) || '120';
    const height = generator.valueToCode(block, 'HEIGHT', pyGenerator.ORDER_NONE) || '32';
    return `lv_obj_set_size(${handle}, ${width}, ${height})\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_width'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const width = generator.valueToCode(block, 'WIDTH', pyGenerator.ORDER_NONE) || '120';
    return `lv_obj_set_size(${handle}, ${width}, lv_obj_get_height(${handle}))\n`;
  };

  pyGenerator.forBlock['yc_ui_label_set_height'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const height = generator.valueToCode(block, 'HEIGHT', pyGenerator.ORDER_NONE) || '32';
    return `lv_obj_set_size(${handle}, lv_obj_get_width(${handle}), ${height})\n`;
  };

  pyGenerator.forBlock['yc_ui_button_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const api = block.getFieldValue('API') || 'lv_btn_create';
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_button_1');
    return `${objectVar} = ${api}(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_button_set_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return `lv_btn_set_text(${handle}, ${text})\n`;
  };

  pyGenerator.forBlock['yc_ui_input_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_input_1');
    return `${objectVar} = lv_textarea_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_input_set_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return `lv_textarea_set_text(${handle}, ${text})\n`;
  };

  pyGenerator.forBlock['yc_ui_slider_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_slider_1');
    return `${objectVar} = lv_slider_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_slider_set_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '0';
    return `lv_slider_set_value(${handle}, ${value}, 0)\n`;
  };

  pyGenerator.forBlock['yc_ui_progress_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_progress_1');
    return `${objectVar} = lv_bar_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_progress_set_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '0';
    return `lv_bar_set_value(${handle}, ${value}, 0)\n`;
  };

  pyGenerator.forBlock['yc_ui_image_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_image_1');
    return `${objectVar} = lv_img_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_image_set_src'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const src = generator.valueToCode(block, 'SRC', pyGenerator.ORDER_NONE) || '""';
    return `lv_img_set_src(${handle}, ${src})\n`;
  };

  pyGenerator.forBlock['yc_ui_obj_center'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    return `lv_obj_center(${handle})\n`;
  };

  pyGenerator.forBlock['yc_ui_obj_set_scrollbar_mode'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const mode = block.getFieldValue('MODE') || 'LV_SCROLLBAR_MODE_AUTO';
    return `lv_obj_set_scrollbar_mode(${handle}, ${mode})\n`;
  };

  pyGenerator.forBlock['yc_ui_obj_add_flag'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const flag = block.getFieldValue('FLAG') || 'LV_OBJ_FLAG_CLICKABLE';
    return `lv_obj_add_flag(${handle}, ${flag})\n`;
  };

  pyGenerator.forBlock['yc_ui_obj_clear_flag'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const flag = block.getFieldValue('FLAG') || 'LV_OBJ_FLAG_CLICKABLE';
    return `lv_obj_clear_flag(${handle}, ${flag})\n`;
  };

  pyGenerator.forBlock['yc_ui_obj_has_flag'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const flag = block.getFieldValue('FLAG') || 'LV_OBJ_FLAG_CLICKABLE';
    return [`lv_obj_has_flag(${handle}, ${flag})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_label_set_long_mode'] = function (block, generator) {
    const handle = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const mode = block.getFieldValue('MODE') || 'LV_LABEL_LONG_WRAP';
    return `lv_label_set_long_mode(${handle}, ${mode})\n`;
  };

  pyGenerator.forBlock['yc_ui_checkbox_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_checkbox_1');
    return `${objectVar} = lv_checkbox_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_checkbox_set_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return `lv_checkbox_set_text(${handle}, ${text})\n`;
  };

  pyGenerator.forBlock['yc_ui_checkbox_get_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`lv_checkbox_get_text(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_switch_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_switch_1');
    return `${objectVar} = lv_switch_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_input_add_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return `lv_textarea_add_text(${handle}, ${text})\n`;
  };

  pyGenerator.forBlock['yc_ui_input_get_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`lv_textarea_get_text(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_input_set_placeholder'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return `lv_textarea_set_placeholder_text(${handle}, ${text})\n`;
  };

  pyGenerator.forBlock['yc_ui_slider_set_mode'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const mode = block.getFieldValue('MODE') || 'LV_SLIDER_MODE_NORMAL';
    return `lv_slider_set_mode(${handle}, ${mode})\n`;
  };

  pyGenerator.forBlock['yc_ui_slider_get_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`lv_slider_get_value(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_progress_set_range'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const min = generator.valueToCode(block, 'MIN', pyGenerator.ORDER_NONE) || '0';
    const max = generator.valueToCode(block, 'MAX', pyGenerator.ORDER_NONE) || '100';
    return `lv_bar_set_range(${handle}, ${min}, ${max})\n`;
  };

  pyGenerator.forBlock['yc_ui_progress_set_mode'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const mode = block.getFieldValue('MODE') || 'LV_BAR_MODE_NORMAL';
    return `lv_bar_set_mode(${handle}, ${mode})\n`;
  };

  pyGenerator.forBlock['yc_ui_progress_get_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`lv_bar_get_value(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_arc_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_arc_1');
    return `${objectVar} = lv_arc_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_arc_set_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const value = generator.valueToCode(block, 'VALUE', pyGenerator.ORDER_NONE) || '0';
    return `lv_arc_set_value(${handle}, ${value})\n`;
  };

  pyGenerator.forBlock['yc_ui_arc_set_range'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const min = generator.valueToCode(block, 'MIN', pyGenerator.ORDER_NONE) || '0';
    const max = generator.valueToCode(block, 'MAX', pyGenerator.ORDER_NONE) || '100';
    return `lv_arc_set_range(${handle}, ${min}, ${max})\n`;
  };

  pyGenerator.forBlock['yc_ui_arc_set_angles'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const start = generator.valueToCode(block, 'START', pyGenerator.ORDER_NONE) || '0';
    const end = generator.valueToCode(block, 'END', pyGenerator.ORDER_NONE) || '360';
    return `lv_arc_set_angles(${handle}, ${start}, ${end})\n`;
  };

  pyGenerator.forBlock['yc_ui_arc_set_bg_angles'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const start = generator.valueToCode(block, 'START', pyGenerator.ORDER_NONE) || '0';
    const end = generator.valueToCode(block, 'END', pyGenerator.ORDER_NONE) || '360';
    return `lv_arc_set_bg_angles(${handle}, ${start}, ${end})\n`;
  };

  pyGenerator.forBlock['yc_ui_arc_set_rotation'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const rotation = generator.valueToCode(block, 'ROTATION', pyGenerator.ORDER_NONE) || '0';
    return `lv_arc_set_rotation(${handle}, ${rotation})\n`;
  };

  pyGenerator.forBlock['yc_ui_arc_set_mode'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const mode = block.getFieldValue('MODE') || 'LV_ARC_MODE_NORMAL';
    return `lv_arc_set_mode(${handle}, ${mode})\n`;
  };

  pyGenerator.forBlock['yc_ui_arc_get_value'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`lv_arc_get_value(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_dropdown_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_dropdown_1');
    return `${objectVar} = lv_dropdown_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_dropdown_set_options'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const options = generator.valueToCode(block, 'OPTIONS', pyGenerator.ORDER_NONE) || '""';
    return `lv_dropdown_set_options(${handle}, ${options})\n`;
  };

  pyGenerator.forBlock['yc_ui_dropdown_add_option'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const option = generator.valueToCode(block, 'OPTION', pyGenerator.ORDER_NONE) || '""';
    const position = generator.valueToCode(block, 'POSITION', pyGenerator.ORDER_NONE) || '0';
    return `lv_dropdown_add_option(${handle}, ${option}, ${position})\n`;
  };

  pyGenerator.forBlock['yc_ui_dropdown_clear_options'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return `lv_dropdown_clear_options(${handle})\n`;
  };

  pyGenerator.forBlock['yc_ui_dropdown_set_selected'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const selected = generator.valueToCode(block, 'SELECTED', pyGenerator.ORDER_NONE) || '0';
    return `lv_dropdown_set_selected(${handle}, ${selected})\n`;
  };

  pyGenerator.forBlock['yc_ui_dropdown_get_selected'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`lv_dropdown_get_selected(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_dropdown_get_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`lv_dropdown_get_text(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_list_create'] = function (block, generator) {
    const parentFromInput = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE);
    const parent = parentFromInput
      || (block.getField('PARENT_VAR')
        ? getVariableSymbolName(block, 'PARENT_VAR', generator, 'ui_screen_1')
        : 'None');
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_list_1');
    return `${objectVar} = lv_list_create(${parent})\n`;
  };

  pyGenerator.forBlock['yc_ui_list_add_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return [`lv_list_add_text(${handle}, ${text})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_list_add_btn'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const symbol = block.getFieldValue('SYMBOL') || 'NULL';
    const symbolCode = symbol === 'NULL' ? 'None' : symbol;
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return [`lv_list_add_btn(${handle}, ${symbolCode}, ${text})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['yc_ui_list_get_btn_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const btn = generator.valueToCode(block, 'BTN', pyGenerator.ORDER_NONE) || 'None';
    return [`lv_list_get_btn_text(${handle}, ${btn})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['cs_ui_screen_create'] = function (block, generator) {
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_screen_1');
    return `${objectVar} = ui_scr_create()\n`;
  };

  pyGenerator.forBlock['cs_ui_screen_show'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return `ui_scr_show(${handle})\n`;
  };

  pyGenerator.forBlock['cs_ui_lable_create'] = function (block, generator) {
    const objectVar = getVariableSymbolName(block, 'OBJ_VAR', generator, 'ui_label_1');
    const parent = generator.valueToCode(block, 'PARENT', pyGenerator.ORDER_NONE) || 'None';
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    const x = generator.valueToCode(block, 'X', pyGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', pyGenerator.ORDER_NONE) || '0';
    const w = generator.valueToCode(block, 'W', pyGenerator.ORDER_NONE) || '100';
    const h = generator.valueToCode(block, 'H', pyGenerator.ORDER_NONE) || '40';
    return `${objectVar} = ui_lbl_create(${parent}, ${text}, ${x}, ${y}, ${w}, ${h})\n`;
  };

  pyGenerator.forBlock['cs_ui_label_set_text'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const text = generator.valueToCode(block, 'TEXT', pyGenerator.ORDER_NONE) || '""';
    return `ui_set_text(${handle}, ${text})\n`;
  };

  pyGenerator.forBlock['cs_ui_label_set_font'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const font = getUiSimpleFontValueFromBlock(block, generator, '1');
    return `ui_set_font(${handle}, ${font})\n`;
  };

  pyGenerator.forBlock['cs_ui_label_set_color'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const color = getUiSimpleColorLiteralFromBlock(block, generator, 'COLOR', '0xFFFFFF');
    return `ui_set_color(${handle}, ${color})\n`;
  };

  pyGenerator.forBlock['cs_ui_label_set_align'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const align = getUiSimpleAlignValueFromBlock(block, generator, '0');
    return `ui_set_align(${handle}, ${align})\n`;
  };

  pyGenerator.forBlock['cs_ui_set_xy'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const x = generator.valueToCode(block, 'X', pyGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', pyGenerator.ORDER_NONE) || '0';
    return `ui_set_xy(${handle}, ${x}, ${y})\n`;
  };

  pyGenerator.forBlock['cs_ui_delete'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return `ui_delete(${handle})\n`;
  };

  pyGenerator.forBlock['cs_ui_set_background_color'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const color = getUiSimpleColorLiteralFromBlock(block, generator, 'COLOR', '0xFFFFFF');
    return `ui_set_bg_color(${handle}, ${color})\n`;
  };

  pyGenerator.forBlock['ym_led_set_power'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const state = block.getFieldValue('STATE') || '0';
    return `ym_led_set_power(${handle}, ${Number(state)})\n`;
  };

  pyGenerator.forBlock['ym_led_set_brightness'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const brightness =
      generator.valueToCode(block, 'BRIGHTNESS', pyGenerator.ORDER_NONE)
      || block.getFieldValue('BRIGHTNESS')
      || '0';
    return `ym_led_set_brightness(${handle}, ${brightness})\n`;
  };

  pyGenerator.forBlock['ym_led_flash_once'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const duration = generator.valueToCode(block, 'DURATION', pyGenerator.ORDER_NONE) || '100';
    return `ym_led_flash(${handle}, ${duration})\n`;
  };

  pyGenerator.forBlock['ym_au_play_audio'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const audio = block.getFieldValue('AUDIO') || '1';
    return `ym_au_play_audio(${handle}, ${Number(audio)})\n`;
  };

  pyGenerator.forBlock['ym_au_record_control'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const control = block.getFieldValue('CONTROL') || '0';
    return `ym_au_record_control(${handle}, ${Number(control)})\n`;
  };

  pyGenerator.forBlock['ym_pir_read_status'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_pir_read_status(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_pir_when_approach'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const statements = generator.statementToCode(block, 'DO');
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `def ym_pir_approach_${key}():\n    prev = int(ym_pir_read_status(${handle}))\n    while True:\n        curr = int(ym_pir_read_status(${handle}))\n        if prev == 0 and curr == 1:\n${statements}        prev = curr\n        wait_seconds(0.2)\non_green_flag(ym_pir_approach_${key})\n`;
  };

  pyGenerator.forBlock['ym_pir_when_leave'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const statements = generator.statementToCode(block, 'DO');
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `def ym_pir_leave_${key}():\n    prev = int(ym_pir_read_status(${handle}))\n    while True:\n        curr = int(ym_pir_read_status(${handle}))\n        if prev == 1 and curr == 0:\n${statements}        prev = curr\n        wait_seconds(0.2)\non_green_flag(ym_pir_leave_${key})\n`;
  };

  pyGenerator.forBlock['ym_dlgt_read_light'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_dlgt_read_light(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_beep_set_frequency'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const frequency = generator.valueToCode(block, 'FREQUENCY', pyGenerator.ORDER_NONE) || '440';
    return `ym_beep_set_frequency(${handle}, ${frequency})\n`;
  };

  pyGenerator.forBlock['ym_beep_set_power'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const state = block.getFieldValue('STATE') || '0';
    return `ym_beep_set_power(${handle}, ${Number(state)})\n`;
  };

  pyGenerator.forBlock['ym_ryg_set_light'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const color = block.getFieldValue('COLOR') || 'red';
    const state = block.getFieldValue('STATE') || '0';
    return `ym_ryg_set_light(${handle}, "${color}", ${Number(state)})\n`;
  };

  pyGenerator.forBlock['ym_k3_read_status'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_k3_read_status(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_k3_when_black_pressed'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `def ym_k3_black_${key}():\n    prev = int(ym_k3_read_status(${handle}))\n    while True:\n        curr = int(ym_k3_read_status(${handle}))\n        if (prev & 0x01) == 0 and (curr & 0x01) != 0:\n${statements}        prev = curr\n        wait_seconds(0.2)\non_green_flag(ym_k3_black_${key})\n`;
  };

  pyGenerator.forBlock['ym_k3_when_red_pressed'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `def ym_k3_red_${key}():\n    prev = int(ym_k3_read_status(${handle}))\n    while True:\n        curr = int(ym_k3_read_status(${handle}))\n        if (prev & 0x02) == 0 and (curr & 0x02) != 0:\n${statements}        prev = curr\n        wait_seconds(0.2)\non_green_flag(ym_k3_red_${key})\n`;
  };

  pyGenerator.forBlock['ym_k3_when_green_pressed'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `def ym_k3_green_${key}():\n    prev = int(ym_k3_read_status(${handle}))\n    while True:\n        curr = int(ym_k3_read_status(${handle}))\n        if (prev & 0x04) == 0 and (curr & 0x04) != 0:\n${statements}        prev = curr\n        wait_seconds(0.2)\non_green_flag(ym_k3_green_${key})\n`;
  };

  pyGenerator.forBlock['ym_swusb_set_power'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const state = block.getFieldValue('STATE') || '0';
    return `ym_swusb_set_power(${handle}, ${Number(state)})\n`;
  };

  pyGenerator.forBlock['ym_swusb_set_level'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const level = generator.valueToCode(block, 'LEVEL', pyGenerator.ORDER_NONE) || '100';
    return `ym_swusb_set_level(${handle}, ${level})\n`;
  };

  pyGenerator.forBlock['ym_th1_read_temperature'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_th1_read_temperature(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_th1_read_humidity'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_th1_read_humidity(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_satellites'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_satellites(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_beidou_satellites'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_beidou_satellites(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_gps_satellites'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_gps_satellites(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_speed'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_speed(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_angle'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_angle(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_acceleration'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_acceleration(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_time'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_time(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_position_status'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_position_status(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_altitude'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_gnss_read_altitude(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_latitude'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`float(ym_gnss_read_latitude(${handle}))`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_read_longitude'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`float(ym_gnss_read_longitude(${handle}))`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_gnss_calculate_distance'] = function (block, generator) {
    const latitude1 = generator.valueToCode(block, 'LAT1', pyGenerator.ORDER_NONE) || '0';
    const longitude1 = generator.valueToCode(block, 'LON1', pyGenerator.ORDER_NONE) || '0';
    const latitude2 = generator.valueToCode(block, 'LAT2', pyGenerator.ORDER_NONE) || '0';
    const longitude2 = generator.valueToCode(block, 'LON2', pyGenerator.ORDER_NONE) || '0';
    (generator as unknown as { definitions_: Record<string, string> }).definitions_.import_math = 'import math';
    return [`(lambda lat1, lon1, lat2, lon2: (lambda delta_lat, delta_lon: (lambda a: 6371 * 2 * math.atan2(math.sqrt(a), math.sqrt(1 - a)))(math.sin(delta_lat / 2) ** 2 + math.cos(math.radians(lat1)) * math.cos(math.radians(lat2)) * math.sin(delta_lon / 2) ** 2))(math.radians(lat2 - lat1), math.radians(lon2 - lon1)))(${latitude1}, ${longitude1}, ${latitude2}, ${longitude2})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_us_read_distance'] = function (block, generator) {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_us_read_distance(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['ym_5g_read_signal_strength'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_signal_strength(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_read_imei'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_imei(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_read_operator'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_operator(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_read_iccid'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_iccid(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_read_sim_id'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_sim_id(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_set_sim_id'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const simId = block.getFieldValue('SIM_ID') || '0';
    return `ym_5g_set_sim_id(${handle}, ${Number(simId)})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_data'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_data(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_read_state'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_state(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_read_mqtt_connect'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_mqtt_connect(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_mqtt_connect'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return `ym_mqtt_connect(${handle})\n`;
  };
  pyGenerator.forBlock['ym_5g_mqtt_disconnect'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return `ym_mqtt_disconnect(${handle})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_user'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_user(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_set_user'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const user = generator.valueToCode(block, 'USER', pyGenerator.ORDER_NONE) || '""';
    return `ym_5g_set_user(${handle}, ${user})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_password'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_password(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_set_password'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const password = generator.valueToCode(block, 'PASSWORD', pyGenerator.ORDER_NONE) || '""';
    return `ym_5g_set_password(${handle}, ${password})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_qos'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_qos(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_set_qos'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const qos = generator.valueToCode(block, 'QOS', pyGenerator.ORDER_NONE) || '2';
    return `ym_5g_set_qos(${handle}, ${qos})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_topic'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_topic(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_mqtt_subscribe'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const topic = generator.valueToCode(block, 'TOPIC', pyGenerator.ORDER_NONE) || '""';
    return `ym_mqtt_subscribe(${handle}, ${topic})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_client_id'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_client_id(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_set_client_id'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const clientId = generator.valueToCode(block, 'CLIENT_ID', pyGenerator.ORDER_NONE) || '""';
    return `ym_5g_set_client_id(${handle}, ${clientId})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_host'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_host(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_set_host'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const host = generator.valueToCode(block, 'HOST', pyGenerator.ORDER_NONE) || '""';
    return `ym_5g_set_host(${handle}, ${host})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_port'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_port(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_set_port'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const port = generator.valueToCode(block, 'PORT', pyGenerator.ORDER_NONE) || '1883';
    return `ym_5g_set_port(${handle}, ${port})\n`;
  };
  pyGenerator.forBlock['ym_5g_set_mqtt_config'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const clientId = generator.valueToCode(block, 'CLIENT_ID', pyGenerator.ORDER_NONE) || '"YM-5G-XXXXX"';
    const host = generator.valueToCode(block, 'HOST', pyGenerator.ORDER_NONE) || '"broker.emqx.io"';
    const port = generator.valueToCode(block, 'PORT', pyGenerator.ORDER_NONE) || '1883';
    const user = generator.valueToCode(block, 'USER', pyGenerator.ORDER_NONE) || '"user"';
    const password = generator.valueToCode(block, 'PASSWORD', pyGenerator.ORDER_NONE) || '"password"';
    return `ym_5g_set_mqtt_config(${handle}, ${clientId}, ${host}, ${port}, ${user}, ${password})\n`;
  };
  pyGenerator.forBlock['ym_5g_mqtt_publish'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const topic = generator.valueToCode(block, 'TOPIC', pyGenerator.ORDER_NONE) || '""';
    const message = generator.valueToCode(block, 'MESSAGE', pyGenerator.ORDER_NONE) || '""';
    return `ym_mqtt_publish(${handle}, ${topic}, ${message})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_subscribe'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_subscribe(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };
  pyGenerator.forBlock['ym_5g_on_subscribe_message'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    const payloadVar = block.getField('PAYLOAD_VAR')?.getText() || block.getFieldValue('PAYLOAD_VAR') || 'mqtt_msg';
    const safeVar = String(payloadVar).replace(/[^a-zA-Z0-9_]/g, '_') || 'mqtt_msg';
    const statements = generator.statementToCode(block, 'DO');
    const key = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
    return `def ym_5g_mqtt_msg_${key}():\n    prev = str(ym_5g_read_subscribe(${handle}))\n    while True:\n        curr = str(ym_5g_read_subscribe(${handle}))\n        if curr and curr != prev:\n            ${safeVar} = curr\n${statements}        prev = curr\n        wait_seconds(0.2)\non_green_flag(ym_5g_mqtt_msg_${key})\n`;
  };
  pyGenerator.forBlock['ym_5g_read_mqtt_state'] = (block, generator) => {
    const handle = generator.valueToCode(block, 'HANDLE', pyGenerator.ORDER_NONE) || 'None';
    return [`ym_5g_read_mqtt_state(${handle})`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['three_create_object'] = function (block) {
    const shape = block.getFieldValue('SHAPE') || 'cube';
    return [`create_3d_object('${shape}')`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['three_transform'] = function (block, generator) {
    const target = generator.valueToCode(block, 'TARGET', pyGenerator.ORDER_NONE) || 'obj';
    const mode = block.getFieldValue('MODE') || 'move';
    const x = generator.valueToCode(block, 'X', pyGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', pyGenerator.ORDER_NONE) || '0';
    const z = generator.valueToCode(block, 'Z', pyGenerator.ORDER_NONE) || '0';
    return `transform_3d_object(${target}, '${mode}', ${x}, ${y}, ${z})\n`;
  };

  pyGenerator.forBlock['three_on_key'] = function (block, generator) {
    const key = block.getFieldValue('KEY') || 'ArrowUp';
    const statements = generator.statementToCode(block, 'DO');
    const safeKey = key.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    return `def on_key_${safeKey}():\n${statements}on_key_press('${key}', on_key_${safeKey})\n`;
  };

  pyGenerator.forBlock['three_on_collision'] = function (block, generator) {
    const target = generator.valueToCode(block, 'TARGET', pyGenerator.ORDER_NONE) || 'obj';
    const statements = generator.statementToCode(block, 'DO');
    return `def on_collision():\n${statements}on_collision(${target}, on_collision)\n`;
  };

  pyGenerator.forBlock['scratch_stage_switch_backdrop'] = function (block, generator) {
    const backdrop = generator.valueToCode(block, 'BACKDROP', pyGenerator.ORDER_NONE) || '"背景"';
    return `stage_switch_backdrop(${backdrop})\n`;
  };

  pyGenerator.forBlock['scratch_stage_next_backdrop'] = function () {
    return 'stage_next_backdrop()\n';
  };

  pyGenerator.forBlock['scratch_stage_set_mode'] = function (block) {
    const mode = block.getFieldValue('MODE') || 'flat';
    return `stage_set_mode("${mode}")\n`;
  };

  pyGenerator.forBlock['scratch_stage_show'] = function () {
    return 'stage_show()\n';
  };

  pyGenerator.forBlock['scratch_stage_hide'] = function () {
    return 'stage_hide()\n';
  };

  pyGenerator.forBlock['scratch_motion_move_steps'] = function (block, generator) {
    const steps = generator.valueToCode(block, 'STEPS', pyGenerator.ORDER_NONE) || '10';
    return `move_steps(${steps})\n`;
  };

  pyGenerator.forBlock['scratch_motion_turn_right'] = function (block, generator) {
    const degrees = generator.valueToCode(block, 'DEGREES', pyGenerator.ORDER_NONE) || '15';
    return `turn_right(${degrees})\n`;
  };

  pyGenerator.forBlock['scratch_motion_turn_left'] = function (block, generator) {
    const degrees = generator.valueToCode(block, 'DEGREES', pyGenerator.ORDER_NONE) || '15';
    return `turn_left(${degrees})\n`;
  };

  pyGenerator.forBlock['scratch_motion_go_to_xy'] = function (block, generator) {
    const x = generator.valueToCode(block, 'X', pyGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', pyGenerator.ORDER_NONE) || '0';
    return `go_to_xy(${x}, ${y})\n`;
  };

  pyGenerator.forBlock['scratch_motion_glide_to_xy'] = function (block, generator) {
    const secs = generator.valueToCode(block, 'SECS', pyGenerator.ORDER_NONE) || '1';
    const x = generator.valueToCode(block, 'X', pyGenerator.ORDER_NONE) || '0';
    const y = generator.valueToCode(block, 'Y', pyGenerator.ORDER_NONE) || '0';
    return `glide_to_xy(${secs}, ${x}, ${y})\n`;
  };

  pyGenerator.forBlock['scratch_motion_point_in_direction'] = function (block, generator) {
    const direction = generator.valueToCode(block, 'DIRECTION', pyGenerator.ORDER_NONE) || '90';
    return `point_in_direction(${direction})\n`;
  };

  pyGenerator.forBlock['scratch_motion_set_x'] = function (block, generator) {
    const x = generator.valueToCode(block, 'X', pyGenerator.ORDER_NONE) || '0';
    return `set_x(${x})\n`;
  };

  pyGenerator.forBlock['scratch_motion_set_y'] = function (block, generator) {
    const y = generator.valueToCode(block, 'Y', pyGenerator.ORDER_NONE) || '0';
    return `set_y(${y})\n`;
  };

  pyGenerator.forBlock['scratch_motion_change_x'] = function (block, generator) {
    const dx = generator.valueToCode(block, 'DX', pyGenerator.ORDER_NONE) || '10';
    return `change_x(${dx})\n`;
  };

  pyGenerator.forBlock['scratch_motion_change_y'] = function (block, generator) {
    const dy = generator.valueToCode(block, 'DY', pyGenerator.ORDER_NONE) || '10';
    return `change_y(${dy})\n`;
  };

  pyGenerator.forBlock['scratch_motion_if_on_edge_bounce'] = function () {
    return 'if_on_edge_bounce()\n';
  };

  pyGenerator.forBlock['scratch_motion_x_position'] = function () {
    return ['get_x_position()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_motion_y_position'] = function () {
    return ['get_y_position()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_motion_direction'] = function () {
    return ['get_direction()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_stage_set_speech_voice'] = function (block) {
    return `set_stage_speech_voice(${JSON.stringify(block.getFieldValue('VOICE') || '')})\n`;
  };

  pyGenerator.forBlock['scratch_looks_say'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', pyGenerator.ORDER_NONE) || '"Hello"';
    return `say(${message})\n`;
  };

  pyGenerator.forBlock['scratch_looks_say_for_secs'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', pyGenerator.ORDER_NONE) || '"Hello"';
    const secs = generator.valueToCode(block, 'SECS', pyGenerator.ORDER_NONE) || '2';
    return `say_for_seconds(${message}, ${secs})\n`;
  };

  pyGenerator.forBlock['scratch_looks_think'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', pyGenerator.ORDER_NONE) || '"Hmm"';
    return `think(${message})\n`;
  };

  pyGenerator.forBlock['scratch_looks_think_for_secs'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', pyGenerator.ORDER_NONE) || '"Hmm"';
    const secs = generator.valueToCode(block, 'SECS', pyGenerator.ORDER_NONE) || '2';
    return `think_for_seconds(${message}, ${secs})\n`;
  };

  pyGenerator.forBlock['scratch_looks_show'] = function () {
    return 'show_sprite()\n';
  };

  pyGenerator.forBlock['scratch_looks_hide'] = function () {
    return 'hide_sprite()\n';
  };

  pyGenerator.forBlock['scratch_looks_switch_costume'] = function (block, generator) {
    const costume = generator.valueToCode(block, 'COSTUME', pyGenerator.ORDER_NONE) || '"造型1"';
    return `switch_costume(${costume})\n`;
  };

  pyGenerator.forBlock['scratch_looks_next_costume'] = function () {
    return 'next_costume()\n';
  };

  pyGenerator.forBlock['scratch_looks_change_size'] = function (block, generator) {
    const size = generator.valueToCode(block, 'SIZE', pyGenerator.ORDER_NONE) || '10';
    return `change_size(${size})\n`;
  };

  pyGenerator.forBlock['scratch_looks_set_size'] = function (block, generator) {
    const size = generator.valueToCode(block, 'SIZE', pyGenerator.ORDER_NONE) || '100';
    return `set_size(${size})\n`;
  };

  pyGenerator.forBlock['scratch_looks_size'] = function () {
    return ['get_size()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sound_play'] = function (block, generator) {
    const sound = generator.valueToCode(block, 'SOUND', pyGenerator.ORDER_NONE) || '"sound1"';
    return `play_sound(${sound})\n`;
  };

  pyGenerator.forBlock['scratch_sound_play_until_done'] = function (block, generator) {
    const sound = generator.valueToCode(block, 'SOUND', pyGenerator.ORDER_NONE) || '"sound1"';
    return `play_sound_until_done(${sound})\n`;
  };

  pyGenerator.forBlock['scratch_sound_stop_all'] = function () {
    return 'stop_all_sounds()\n';
  };

  pyGenerator.forBlock['scratch_sound_change_volume'] = function (block, generator) {
    const volume = generator.valueToCode(block, 'VOLUME', pyGenerator.ORDER_NONE) || '10';
    return `change_volume(${volume})\n`;
  };

  pyGenerator.forBlock['scratch_sound_set_volume'] = function (block, generator) {
    const volume = generator.valueToCode(block, 'VOLUME', pyGenerator.ORDER_NONE) || '100';
    return `set_volume(${volume})\n`;
  };

  pyGenerator.forBlock['scratch_sound_volume'] = function () {
    return ['get_volume()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  const pythonGeneratorWithScrub = pyGenerator as unknown as GeneratorWithScrub;
  const originalPythonScrub = pythonGeneratorWithScrub.scrub_;
  pythonGeneratorWithScrub.scrub_ = function (block, code, thisOnly) {
    if (
      block.type === 'scratch_event_when_green_flag' ||
      block.type === 'scratch_event_when_key_pressed' ||
      block.type === 'scratch_event_when_sprite_clicked' ||
      block.type === 'ym_k3_when_black_pressed' ||
      block.type === 'ym_k3_when_red_pressed' ||
      block.type === 'ym_k3_when_green_pressed'
    ) {
      return code;
    }
    return originalPythonScrub.call(this, block, code, thisOnly);
  };

  pyGenerator.forBlock['scratch_event_when_green_flag'] = function (block, generator) {
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `def on_green_flag():\n${statements}on_green_flag(on_green_flag)\n`;
  };

  pyGenerator.forBlock['scratch_event_when_key_pressed'] = function (block, generator) {
    const key = block.getFieldValue('KEY') || 'Space';
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `def on_key_${key}():\n${statements}on_key_pressed("${key}", on_key_${key})\n`;
  };

  pyGenerator.forBlock['scratch_event_when_sprite_clicked'] = function (block, generator) {
    const statements = generator.blockToCode(block.getNextBlock()) || '';
    return `def on_sprite_clicked():\n${statements}on_sprite_clicked(on_sprite_clicked)\n`;
  };

  pyGenerator.forBlock['scratch_event_broadcast'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', pyGenerator.ORDER_NONE) || '"message1"';
    return `broadcast(${message})\n`;
  };

  pyGenerator.forBlock['scratch_event_broadcast_and_wait'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', pyGenerator.ORDER_NONE) || '"message1"';
    return `broadcast_and_wait(${message})\n`;
  };

  pyGenerator.forBlock['scratch_event_when_receive'] = function (block, generator) {
    const message = generator.valueToCode(block, 'MESSAGE', pyGenerator.ORDER_NONE) || '"message1"';
    const statements = generator.statementToCode(block, 'DO');
    return `def on_broadcast_${block.id.replace(/-/g, '_')}(msg):\n${statements}on_broadcast(${message}, on_broadcast_${block.id.replace(/-/g, '_')})\n`;
  };

  pyGenerator.forBlock['scratch_control_wait'] = function (block, generator) {
    const secs = generator.valueToCode(block, 'SECS', pyGenerator.ORDER_NONE) || '1';
    return `wait_seconds(${secs})\n`;
  };

  pyGenerator.forBlock['scratch_control_system_beep'] = function (block, generator) {
    const duration = generator.valueToCode(block, 'DURATION', pyGenerator.ORDER_NONE) || '200';
    const freq = generator.valueToCode(block, 'FREQ', pyGenerator.ORDER_NONE) || '880';
    return `system_beep(${duration}, ${freq})\n`;
  };

  pyGenerator.forBlock['scratch_control_wait_until'] = function (block, generator) {
    const condition = generator.valueToCode(block, 'CONDITION', pyGenerator.ORDER_NONE) || 'False';
    return `wait_until(lambda: ${condition})\n`;
  };

  pyGenerator.forBlock['scratch_control_repeat'] = function (block, generator) {
    const times = generator.valueToCode(block, 'TIMES', pyGenerator.ORDER_NONE) || '10';
    const statements = generator.statementToCode(block, 'DO');
    return `for _ in range(int(${times})):\n${statements}`;
  };

  pyGenerator.forBlock['scratch_control_forever'] = function (block, generator) {
    const statements = generator.statementToCode(block, 'DO');
    return `while True:\n${statements}`;
  };

  pyGenerator.forBlock['scratch_control_repeat_until'] = function (block, generator) {
    const condition = generator.valueToCode(block, 'CONDITION', pyGenerator.ORDER_NONE) || 'False';
    const statements = generator.statementToCode(block, 'DO');
    return `while not (${condition}):\n${statements}`;
  };

  pyGenerator.forBlock['scratch_control_stop'] = function (block) {
    const scope = block.getFieldValue('SCOPE') || 'all';
    return `stop_scripts("${scope}")\n`;
  };

  pyGenerator.forBlock['scratch_control_create_clone_of'] = function (block, generator) {
    const target = generator.valueToCode(block, 'TARGET', pyGenerator.ORDER_NONE) || '"自己"';
    return `create_clone_of(${target})\n`;
  };

  pyGenerator.forBlock['scratch_control_delete_clone'] = function () {
    return 'delete_this_clone()\n';
  };

  pyGenerator.forBlock['scratch_sensing_touching'] = function (block) {
    const target = block.getFieldValue('TARGET') || 'mouse';
    return [`is_touching("${target}")`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_touching_color'] = function (block) {
    const color = block.getFieldValue('COLOR') || '#ff0000';
    return [`is_touching_color("${color}")`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_color_touching_color'] = function (block) {
    const color1 = block.getFieldValue('COLOR1') || '#ff0000';
    const color2 = block.getFieldValue('COLOR2') || '#0000ff';
    return [`is_color_touching_color("${color1}", "${color2}")`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_distance_to'] = function (block) {
    const target = block.getFieldValue('TARGET') || 'mouse';
    return [`distance_to("${target}")`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_ask_and_wait'] = function (block, generator) {
    const question = generator.valueToCode(block, 'QUESTION', pyGenerator.ORDER_NONE) || '"?"';
    return `ask_and_wait(${question})\n`;
  };

  pyGenerator.forBlock['scratch_sensing_answer'] = function () {
    return ['get_answer()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_key_pressed'] = function (block) {
    const key = block.getFieldValue('KEY') || 'Space';
    return [`is_key_pressed("${key}")`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_mouse_down'] = function () {
    return ['is_mouse_down()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_mouse_x'] = function () {
    return ['mouse_x()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_mouse_y'] = function () {
    return ['mouse_y()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_loudness'] = function () {
    return ['loudness()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_timer'] = function () {
    return ['timer()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_reset_timer'] = function () {
    return 'reset_timer()\n';
  };

  pyGenerator.forBlock['scratch_sensing_current_time'] = function (block) {
    const unit = block.getFieldValue('UNIT') || 'second';
    return [`current_time("${unit}")`, pyGenerator.ORDER_FUNCTION_CALL];
  };

  pyGenerator.forBlock['scratch_sensing_days_since_2000'] = function () {
    return ['days_since_2000()', pyGenerator.ORDER_FUNCTION_CALL];
  };

  Blockly.Blocks['cscript_preserved_source'] = {
    init() {
      this.appendDummyInput()
        .appendField('保留的 CScript 源码')
        .appendField(new Blockly.FieldMultilineInput(''), 'CODE');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(20);
      this.setTooltip('导入器无法完全转换的 CScript 会保留在此积木中。生成 C 代码时会原样输出，确保不丢失已验证的逻辑。');
    },
  };
};

export function registerBlocks(): void {
  ensureRegistered();
}

export default registerBlocks;
